import "./styles.css";
import QRCode from "qrcode";
import {
  getPianoTutorCore,
  midiToNoteName,
  listPieces,
  labelForMidi,
  defaultCPositionFinger,
  getCoachLessonsForClass,
  getExamBriefing,
  MAX_CURRICULUM_STAGE,
  STAGE_EXAMS,
  PRACTICE_PACKS,
  recommendRevision,
  buildReportFromStats,
  computeClassSessionScore,
  getClassById,
  getKeyboardRange,
  guessKeyboardSizeFromDeviceName,
  snapObservedRangeToKeyboard,
  expandRangeToCover,
  KEYBOARD_SIZE_OPTIONS,
  type CoreEngineStatus,
  type TutorNoteEvent,
  type TimeSignatureId,
  type MetronomeBeatEvent,
  type PlayInputMode,
  type MusicPiece,
  type NotationMode,
  type CoachLesson,
  type FollowAlongStats,
  type KeyboardSizeId,
  type ImportedPracticeMode,
} from "@piano-tutor/shared";
import { PianoCanvas } from "./pianoCanvas";
import { StaffCanvas } from "./staffCanvas";
import { TutorProbe } from "./probe";
import { CoachCanvas } from "./coachCanvas";
import { GameFxBackdrop } from "./gameFx";
import { HitParticleLayer } from "./hitParticles";
import { HandsGuideOverlay } from "./handsGuide";
import { LevelMapCanvas, type StageNode } from "./levelMap";
import { sfx } from "./sfx/SfxEngine";
import { ResultsOverlay, type ResultsCloseAction } from "./results/ResultsOverlay";
import { ArcadeHost } from "./arcade/ArcadeHost";
import { runCompanySplash } from "./splash/companySplash";
import { bindAccountUi, compressAvatarFile } from "./accountUi";
import { getAccountSnapshot, saveAccountPhoto } from "./firebase/authAccount";
import {
  evaluateCertificateEligibility,
  printCertificateSheet,
} from "./certificate";
import {
  beginVerifiedExam,
  issueCourseCertificate,
  submitVerifiedExam,
  type VerifiedCertificate,
} from "./firebase/certificateService";
import { consumeInviteParam, shareAppInvite } from "./share";
import { shareCertificateCard } from "./shareCertificate";
import { shareProgressCard } from "./shareProgress";
import { readLastCampaign, saveLastCampaign } from "./campaignResume";
import { startTour, shouldAutoTour } from "./tour";
import {
  loadStreak,
  recordPractice,
  renderStreakBar,
  renderStreakSignInGate,
  streakAccountUid,
} from "./streaks";
import { isFirebaseConfigured } from "./firebase/config";
import { consumeReferralParam } from "./referral";
import { bindShareEarnUi, focusShareEarnPanel } from "./shareEarnUi";
import {
  listSavedMidis,
  saveMidiFile,
  getSavedMidi,
  deleteSavedMidi,
} from "./midiLibrary";
import {
  bumpSessionClears,
  shouldShowBuyPrompt,
  markBuyPromptShown,
  dismissBuyPromptForever,
  shouldShowRatePrompt,
  markRatePromptShown,
  dismissRatePromptForever,
  PLAY_STORE_URL,
} from "./engagementPrompts";
import { bindHelpButtons, helpContactButtonsHtml } from "./support";
import {
  FREE_TRIAL_LESSON_COUNT,
  freeTrialCompletedCount,
  isClassInFreeTrial,
} from "./freeTrial";
import {
  bindProgressCloudSync,
  readLocalProgress,
  saveProgressEverywhere,
} from "./firebase/progressSync";
import {
  areAdsEnabled,
  configureAdsForAccount,
  initAds,
  maybeShowNaturalInterstitial,
  setAdSurface,
  showRewardedAdFreeBreak,
} from "./native/ads";
import {
  isNativePianoAudioAvailable,
  nativePianoAllNotesOff,
  nativePianoNoteOff,
  nativePianoNoteOn,
  nativePianoSetMasterVolume,
  nativePianoSetMuted,
  nativePianoStart,
} from "./native/nativePianoAudio";
import {
  desktopPianoAllNotesOff,
  desktopPianoNoteOff,
  desktopPianoNoteOn,
  desktopPianoSetMasterVolume,
  desktopPianoSetMuted,
  desktopPianoStart,
  isDesktopPianoAvailable,
} from "./native/desktopPianoAudio";

void runCompanySplash();
void initAds();
void import("./analytics").then((m) => m.startClientAnalytics());

const app = document.querySelector<HTMLDivElement>("#app")!;

app.innerHTML = `
  <div class="game-shell" data-mode="home" id="game-shell">
    <div class="top-bar">
      <button type="button" class="secondary" id="btn-home">⟵ Hub</button>
      <span class="brand-mini">Ultimate Piano Tutor</span>
      <span class="stat-pill" id="firebase-ready-pill">Offline</span>
      <span class="stat-pill" id="top-rank">Rank <strong>TRAINEE</strong></span>
    </div>

    <nav class="game-modes" aria-label="Game modes">
      <button type="button" class="mode-btn" data-mode="home">Hub</button>
      <button type="button" class="mode-btn" data-mode="campaign">Campaign</button>
      <button type="button" class="mode-btn" data-mode="play">Practice</button>
      <button type="button" class="mode-btn" data-mode="arcade">Arcade</button>
      <button type="button" class="mode-btn" data-mode="vault">Vault</button>
      <button type="button" class="mode-btn" data-mode="profile">Profile</button>
      <button type="button" class="mode-btn" data-mode="gear">Gear</button>
    </nav>

    <section class="game-screen is-active" data-screen="home" id="screen-home">
      <div class="home-hero">
        <span class="phase-badge">Learn · Practice · Master</span>
        <h1 class="brand brand-3d" aria-label="Ultimate Piano Tutor">
          <span class="brand-depth" aria-hidden="true">ULTIMATE PIANO TUTOR</span>
          <span class="brand-mid" aria-hidden="true">ULTIMATE PIANO TUTOR</span>
          <span class="brand-face">ULTIMATE PIANO TUTOR</span>
        </h1>
        <p class="tagline">Classic piano mastery, one lesson at a time.</p>
        <div class="credits-plaque" aria-label="Game credits">
          <span class="credits-plaque-kicker">By</span>
          <strong class="credits-plaque-studio">LOTI Games and Apps</strong>
          <strong class="credits-plaque-names">SHRIKANT &amp; SUMNNA BHARDWAJ</strong>
        </div>
      </div>

      <div class="home-layout">
        <div class="profile-orbit">
          <div class="profile-card">
            <div class="avatar-wrap">
              <img id="profile-avatar" class="avatar" alt="Player avatar" width="84" height="84" referrerpolicy="no-referrer" />
              <button type="button" class="secondary avatar-edit" id="btn-avatar" title="Change photo">✎</button>
              <input type="file" id="avatar-file" accept="image/*" hidden />
            </div>
            <div class="profile-fields">
              <div class="profile-name-row">
                <input type="text" id="profile-name" maxlength="24" placeholder="ENTER PLAYER NAME" spellcheck="false" />
              </div>
              <div class="auth-strip" id="auth-strip">
                <span class="auth-status" id="auth-status">Signed out</span>
                <span class="stat-pill" id="auth-plan">FREE · 5 lessons</span>
                <span class="stat-pill admin-badge" data-admin-only hidden>Admin</span>
                <div class="auth-actions">
                  <button type="button" id="btn-auth-google" class="auth-chip btn-gold" data-auth="google">Sign in with Google</button>
                  <button type="button" id="btn-auth-guest" class="auth-chip" data-auth="guest">Guest</button>
                  <button type="button" id="btn-auth-out" class="auth-chip" data-auth="out" hidden>Sign out</button>
                  <button type="button" class="auth-chip btn-admin-portal" data-admin-only data-admin-portal hidden>Admin</button>
                  <button type="button" class="auth-chip" data-portal="profile">Profile</button>
                </div>
                <p class="auth-config-hint" id="auth-config-hint" hidden>
                  Sign-in unavailable until Firebase is configured.
                </p>
              </div>
              <div class="profile-stats">
                <span class="stat-pill" id="hud-stage">Stage <strong>1/${MAX_CURRICULUM_STAGE}</strong></span>
                <span class="stat-pill" id="hud-rank">Rank <strong>TRAINEE</strong></span>
                <span class="stat-pill" id="hud-xp">XP <strong>0</strong></span>
                <span class="stat-pill" id="hud-clears">Clears <strong>0</strong></span>
              </div>
              <div class="xp-track" title="Season XP"><div class="xp-fill" id="xp-fill"></div></div>
              <div id="streak-bar-slot"></div>
            </div>
          </div>
        </div>

        <div class="command-ring" role="navigation" aria-label="Game modes">
          <div class="ring-core" aria-hidden="true"><span>GO</span></div>
          <button type="button" class="mode-orb is-featured" data-portal="campaign" style="--i:0">
            <span class="orb-glyph">C</span>
            <span class="orb-label">Campaign</span>
            <span class="orb-sub">Lessons</span>
          </button>
          <button type="button" class="mode-orb" data-portal="play" style="--i:1">
            <span class="orb-glyph">P</span>
            <span class="orb-label">Practice</span>
            <span class="orb-sub">Warm-up</span>
          </button>
          <button type="button" class="mode-orb" data-portal="arcade" style="--i:2">
            <span class="orb-glyph">X</span>
            <span class="orb-label">Arcade</span>
            <span class="orb-sub">Games</span>
          </button>
          <button type="button" class="mode-orb" data-portal="vault" style="--i:3">
            <span class="orb-glyph">V</span>
            <span class="orb-label">Vault</span>
            <span class="orb-sub">Music</span>
          </button>
          <button type="button" class="mode-orb" data-portal="gear" style="--i:4">
            <span class="orb-glyph">G</span>
            <span class="orb-label">Gear</span>
            <span class="orb-sub">Setup</span>
          </button>
        </div>
      </div>

      <div class="armory-strip" aria-label="Shop">
        <div class="armory-head">
          <h2>Shop</h2>
          <span class="phase-badge" id="armory-badge">Open</span>
        </div>
        <button type="button" class="earn-hub-banner" data-portal="profile" id="btn-hub-earn">
          <span class="earn-hub-kicker">SHARE &amp; EARN</span>
          <strong>Invite friends → earn ₹250 per Full Course buy</strong>
          <span class="earn-hub-cta">Open wallet</span>
        </button>
        <div class="armory-grid">
          <article class="armory-card is-locked">
            <span class="armory-kicker">Course</span>
            <h3>Full Course</h3>
            <p>All lessons after the 5 free ones · exams · ads off</p>
            <button type="button" class="auth-chip" data-buy="full_course">Buy</button>
          </article>
          <article class="armory-card is-locked">
            <span class="armory-kicker">Audio</span>
            <h3>HD Sample Pack</h3>
            <p>Richer real-piano samples</p>
            <button type="button" class="auth-chip" data-buy="hd_sample_pack" disabled>Coming Soon</button>
          </article>
          <article class="armory-card is-locked">
            <span class="armory-kicker">Cert</span>
            <h3>Course Certificate</h3>
            <p>PDF after all ${STAGE_EXAMS.length} exams</p>
            <button type="button" class="auth-chip" id="btn-certificate-sample">Preview</button>
          </article>
          <article class="armory-card" id="rewarded-ad-card" hidden>
            <span class="armory-kicker">Free</span>
            <h3>Ad-Free Break</h3>
            <p>Watch 1 ad · 30 min quiet</p>
            <button type="button" class="auth-chip" id="btn-rewarded-ad">Watch Ad</button>
          </article>
        </div>
      </div>

      <div class="hub-help" aria-label="Help and support">
        <div class="hub-help-head">
          <h2>Need help?</h2>
          <p>Stuck or have a question? Message us directly — we're here to help.</p>
        </div>
        <div class="help-contact hub-help-actions">
          ${helpContactButtonsHtml()}
        </div>
      </div>
    </section>

    <div id="mode-transition" class="mode-transition" hidden aria-hidden="true">
      <div class="mt-flash"></div>
      <div class="mt-rings"></div>
      <div class="mt-scan"></div>
      <div class="mt-label" id="mt-label">OPEN</div>
    </div>

    <section class="game-screen" data-screen="play">
      <div class="panel practice-main">
        <h2>Practice <span class="panel-sub" id="practice-mode-label">Free warm-up</span></h2>
        <div class="combo-banner" id="combo-banner">Tap a key to unlock audio</div>
        <div id="class-mission-bar" class="class-mission-bar" hidden>
          <div class="mission-row">
            <strong id="mission-title">CLASS LIVE</strong>
            <button type="button" class="secondary" id="btn-arena-abort">Abort</button>
          </div>
          <div class="mission-stats">
            <span title="Time">Time <strong id="mission-timer">0:00</strong></span>
            <span title="Progress">Prog <strong id="mission-prog">0%</strong></span>
            <span>Hits <strong id="mission-hits">0</strong></span>
            <span>Miss <strong id="mission-miss">0</strong></span>
            <span>Acc <strong id="mission-acc">—</strong></span>
            <span id="mission-seg">—</span>
          </div>
          <div class="mission-track"><div class="mission-fill" id="mission-fill"></div></div>
          <p class="mission-note" id="mission-note">~ <span id="mission-est">—</span> min · Abort = not cleared</p>
          <button type="button" class="secondary" id="btn-performance-download" hidden>
            Download MIDI Performance Recording
          </button>
        </div>
        <div id="exam-task-card" class="exam-task-card" hidden>
          <div class="exam-task-kicker">EXAM</div>
          <strong id="exam-task-title">—</strong>
          <p id="exam-task-body">—</p>
          <p class="exam-task-next">Next: <strong id="exam-task-notes">—</strong></p>
        </div>
        <div class="row practice-tools">
          <label>
            Notation
            <select id="notation-mode">
              <option value="letter">Letters</option>
              <option value="solfege">Solfège</option>
              <option value="indian">Indian</option>
              <option value="finger">Fingers</option>
              <option value="staff">Staff</option>
            </select>
          </label>
        </div>
        <div class="piano-stage" id="piano-stage">
          <div class="piano-range-badge" id="piano-range-badge">61-key · C2–C7</div>
          <canvas id="hit-fx" aria-hidden="true"></canvas>
          <canvas id="hands-guide" aria-hidden="true"></canvas>
          <div class="piano-wrap">
            <canvas id="piano" aria-label="Virtual piano keyboard"></canvas>
          </div>
        </div>
        <p class="key-legend">
          <span><i class="key-swatch guide"></i> Expected</span>
          <span><i class="key-swatch wrong"></i> Miss</span>
          <span><i class="key-swatch hit"></i> Hit</span>
        </p>
        <div class="staff-wrap" id="staff-wrap" hidden>
          <canvas id="staff" aria-label="Western staff notation"></canvas>
        </div>
        <p class="hint" id="expected-hint">Green keys = next note. Start a class from Campaign.</p>
      </div>
      <details class="panel practice-fold" id="metronome-panel">
        <summary><h2>Metronome</h2></summary>
        <div class="row">
          <button type="button" id="btn-metro-toggle">Start</button>
          <button type="button" id="btn-metro-reset" class="secondary">Reset</button>
          <label>
            BPM
            <input type="range" id="bpm" min="40" max="240" value="100" />
            <strong id="bpm-val">100</strong>
          </label>
          <label>
            Beat
            <select id="timesig">
              <option value="4/4">4/4</option>
              <option value="3/4">3/4</option>
              <option value="2/4">2/4</option>
              <option value="6/8">6/8</option>
            </select>
          </label>
          <label>
            Click
            <input type="range" id="metro-vol" min="0" max="100" value="55" />
          </label>
          <label>
            <input type="checkbox" id="lock-grade" />
            Score to click
          </label>
        </div>
        <div class="pulse-row" id="pulse-row" aria-live="polite"></div>
        <div class="status-grid" id="metro-stats"></div>
      </details>
      <details class="panel practice-fold">
        <summary><h2>Hit Feed</h2></summary>
        <div class="event-log" id="log" aria-live="polite"></div>
      </details>
    </section>

    <section class="game-screen" data-screen="campaign">
      <div class="panel">
        <h2>Campaign</h2>
        <div class="level-map-wrap"><canvas id="level-map" aria-label="Stage level map"></canvas></div>
        <div class="row">
          <label>
            Stage
            <select id="stage-select"></select>
          </label>
          <label>
            Class
            <select id="class-select"></select>
          </label>
          <label>
            Mins
            <input type="range" id="class-mins" min="8" max="60" value="15" />
            <strong id="class-mins-val">15</strong>
          </label>
        </div>
        <p class="hint" id="class-desc"></p>
        <p class="hint" id="class-plan-meta"></p>
        <div class="row campaign-actions">
          <button type="button" id="btn-class-start">Start Class</button>
          <button type="button" id="btn-exam-start" class="btn-gold">Exam</button>
          <button type="button" id="btn-class-stop" class="secondary">Abort</button>
          <button type="button" id="btn-exam-stop" class="secondary">Abort exam</button>
        </div>
        <div class="coach-panel" id="coach-panel" hidden>
          <h3 id="coach-title">Briefing</h3>
          <div class="coach-wrap"><canvas id="coach-canvas"></canvas></div>
          <ul class="coach-bullets" id="coach-bullets"></ul>
          <div class="row">
            <button type="button" id="btn-coach-next">Next</button>
            <button type="button" id="btn-coach-skip" class="secondary">Skip</button>
          </div>
        </div>
        <div class="exam-brief" id="exam-brief" hidden>
          <p class="exam-brief-eyebrow">GATEKEEPER</p>
          <h3 id="exam-brief-title">Stage exam</h3>
          <p class="hint" id="exam-brief-desc"></p>
          <ul class="exam-brief-rules" id="exam-brief-rules"></ul>
          <ol class="exam-brief-pieces" id="exam-brief-pieces"></ol>
          <p class="exam-brief-meta" id="exam-brief-meta"></p>
          <div class="row">
            <button type="button" id="btn-exam-begin" class="btn-gold">Begin</button>
            <button type="button" id="btn-exam-cancel" class="secondary">Cancel</button>
          </div>
        </div>
        <p class="hint" id="progress-meta">Progress syncs when signed in.</p>
        <div class="status-grid" id="class-session-stats"></div>
      </div>
    </section>

    <section class="game-screen" data-screen="arcade">
      <div class="panel">
        <h2>Arcade</h2>
        <p class="hint" id="arcade-bay-hint" style="margin-top:0;margin-bottom:0.85rem">Pick a game — countdown, then play.</p>
        <div class="arcade-grid" id="arcade-grid"></div>
      </div>
    </section>

    <section class="game-screen" data-screen="vault">
      <div class="panel">
        <h2>Practice Packs</h2>
        <p class="hint">Optional skill labs — they deepen weak areas but do not block exams or your certificate.</p>
        <div class="row">
          <label>
            Pack
            <select id="practice-pack-select"></select>
          </label>
          <button type="button" id="btn-practice-pack">Open Pack</button>
        </div>
        <p class="hint" id="practice-pack-meta"></p>
      </div>
      <div class="panel">
        <h2>Vault</h2>
        <div class="row">
          <label>
            Piece
            <select id="piece-select"></select>
          </label>
          <button type="button" id="btn-piece-play">Demo</button>
          <button type="button" id="btn-piece-learn" class="btn-gold">Learn</button>
          <button type="button" id="btn-piece-perform">Performance</button>
          <button type="button" id="btn-piece-stop" class="secondary">Stop</button>
        </div>
        <p class="hint" id="piece-meta"></p>
        <div class="event-log" id="piece-notes" style="height:100px"></div>
      </div>
      <div class="panel">
        <h2>Import MIDI</h2>
        <div id="midi-drop" class="dropzone" tabindex="0">
          Drop a <strong>.mid</strong> file, or
          <label class="file-link">browse<input type="file" id="midi-file" accept=".mid,.midi,audio/midi" hidden /></label>
        </div>
        <div class="row" style="margin-top:0.75rem">
          <button type="button" id="btn-import-play" disabled>Demo</button>
          <button type="button" id="btn-import-learn" class="btn-gold" disabled>Learn</button>
          <button type="button" id="btn-import-perform" disabled>Performance</button>
          <button type="button" id="btn-import-stop" class="secondary" disabled>Stop</button>
          <button type="button" id="btn-import-clear" class="secondary" disabled>Clear</button>
        </div>
        <p class="hint" id="import-meta">No file yet. Learn waits for the correct key; Performance keeps time and grades hits/misses.</p>
        <h3 class="gear-section" style="margin-top:1rem">Saved on this device</h3>
        <ul class="midi-library-list" id="midi-library-list"></ul>
        <p class="hint" id="midi-library-empty">No saved MIDI yet — import a file to keep it for later practice.</p>
      </div>
    </section>

    <section class="game-screen" data-screen="profile">
      <div class="panel profile-panel">
        <h2>Profile</h2>
        <div class="profile-detail-grid">
          <div class="profile-detail-card earn-card" id="share-earn-root">
            <div class="earn-hero">
              <span class="earn-kicker">SHARE &amp; EARN</span>
              <h3>Invite friends · earn ₹250</h3>
              <p class="earn-tagline">
                When someone buys the <strong>Full Course</strong> with your link, you get
                <strong>₹250</strong> in your wallet (India).
              </p>
            </div>
            <div class="earn-gate-row" id="earn-gate-row">
              <p class="hint" id="earn-gate">Sign in with Google to unlock your referral HQ.</p>
              <button type="button" class="btn-gold" data-auth="google">Sign in with Google</button>
            </div>
            <p class="earn-status" id="earn-status" hidden></p>
            <div id="earn-body" hidden>
              <div class="earn-stats">
                <div class="earn-stat">
                  <span class="earn-stat-label">PENDING</span>
                  <strong id="earn-pending">₹0</strong>
                  <span class="earn-stat-sub">14-day hold</span>
                </div>
                <div class="earn-stat is-hot">
                  <span class="earn-stat-label">AVAILABLE</span>
                  <strong id="earn-available">₹0</strong>
                  <span class="earn-stat-sub">Ready to withdraw</span>
                </div>
                <div class="earn-stat">
                  <span class="earn-stat-label">LIFETIME</span>
                  <strong id="earn-lifetime">₹0</strong>
                  <span class="earn-stat-sub">Total earned</span>
                </div>
              </div>
              <div class="earn-code-row">
                <div>
                  <span class="earn-stat-label">YOUR CODE</span>
                  <div class="earn-code" id="earn-code">————</div>
                </div>
                <button type="button" class="btn-gold" id="btn-earn-share">Share link</button>
                <button type="button" class="secondary" id="btn-earn-copy">Copy link</button>
                <button type="button" class="secondary" id="btn-earn-retry">Retry</button>
              </div>
              <div class="earn-link-label">
                <span>Play Store + Microsoft Store links (code included)</span>
                <div class="earn-link-box">
                  <span class="earn-link-tag">Android · Google Play</span>
                  <div class="earn-link-row">
                    <input type="text" id="earn-link-play" readonly aria-label="Google Play referral link" />
                    <button type="button" class="auth-chip" data-earn-copy="earn-link-play">Copy</button>
                  </div>
                </div>
                <div class="earn-link-box">
                  <span class="earn-link-tag">Windows · Microsoft Store</span>
                  <div class="earn-link-row">
                    <input type="text" id="earn-link-ms" readonly aria-label="Microsoft Store referral link" />
                    <button type="button" class="auth-chip" data-earn-copy="earn-link-ms">Copy</button>
                  </div>
                </div>
              </div>
              <div class="earn-kyc">
                <h4>Payout details (Name + UPI)</h4>
                <div class="row">
                  <label>Name on UPI<input type="text" id="earn-kyc-name" maxlength="80" placeholder="Full name" /></label>
                  <label>UPI ID<input type="text" id="earn-kyc-upi" maxlength="80" placeholder="name@paytm" /></label>
                  <button type="button" class="auth-chip" id="btn-earn-kyc">Save UPI</button>
                </div>
                <button type="button" class="btn-gold" id="btn-earn-withdraw">Withdraw available</button>
                <p class="hint">Min ₹1000 · Manual Paytm/PhonePe payout (1–3 business days).</p>
              </div>
              <div class="earn-lists">
                <div>
                  <h4>Pending unlocks</h4>
                  <ul id="earn-unlocks"></ul>
                </div>
                <div>
                  <h4>Withdrawals</h4>
                  <ul id="earn-withdrawals"></ul>
                </div>
              </div>
              <details class="earn-rules">
                <summary>Share &amp; Earn rules (read before sharing)</summary>
                <ul>
                  <li>You earn <strong>₹250</strong> only when a friend buys the <strong>Full Course</strong> using your referral link/code.</li>
                  <li>Share sends <strong>Google Play</strong> + <strong>Microsoft Store</strong> links (with your code) — no website. Friends can also enter the code at <strong>Sign in</strong> (auto-fills when the invite carries a code).</li>
                  <li>Program is for <strong>India</strong> users first.</li>
                  <li>Reward stays <strong>Pending for 14 days</strong>, then becomes Available.</li>
                  <li>Minimum withdraw <strong>₹1000</strong>. Save Name + UPI first.</li>
                  <li>One referral link per buyer account (first link wins).</li>
                  <li>You cannot apply your own code on the same account.</li>
                  <li>Payouts are manual (Paytm/PhonePe) for now — we mark them Paid after transfer.</li>
                  <li>Purchases are final — see website legal notes (no refund).</li>
                </ul>
              </details>
            </div>
            <p class="earn-legal-foot">
              * Website legal notes:
              <a href="/legal.html" target="_blank" rel="noopener">Privacy · Terms · Referral · No refund</a>
            </p>
          </div>
          <div class="profile-detail-card profile-edit-card">
            <h3>Your look</h3>
            <div class="profile-edit-row">
              <div class="avatar-wrap profile-edit-avatar-wrap">
                <img id="profile-edit-avatar" class="avatar" alt="Profile photo" width="84" height="84" referrerpolicy="no-referrer" />
              </div>
              <div class="profile-edit-fields">
                <label class="profile-edit-label">
                  Display name
                  <input type="text" id="profile-edit-name" maxlength="24" placeholder="Your name" spellcheck="false" />
                </label>
                <div class="row" style="margin:0">
                  <button type="button" class="auth-chip" id="btn-profile-avatar">Change photo</button>
                  <button type="button" class="auth-chip" id="btn-profile-use-google-photo">Google photo</button>
                  <button type="button" class="auth-chip" id="btn-profile-save">Save</button>
                </div>
                <p class="hint" id="profile-save-hint">Edit name or photo, then Save.</p>
              </div>
            </div>
          </div>
          <div class="profile-detail-card">
            <h3>Account</h3>
            <p class="hint">Status <strong id="auth-status-profile">Signed out</strong></p>
            <p class="hint">Name <strong id="profile-account-name">—</strong></p>
            <p class="hint">Email <strong id="auth-email">—</strong></p>
            <p class="hint">Plan <strong id="auth-plan-detail">FREE · 5 lessons</strong></p>
            <p class="hint" data-admin-only hidden>Role <strong class="admin-badge-inline">Admin</strong></p>
            <div class="auth-actions profile-auth-actions">
              <button type="button" class="btn-gold" data-auth="google">Sign in with Google</button>
              <button type="button" class="auth-chip" data-auth="guest">Continue as Guest</button>
              <button type="button" class="auth-chip" data-auth="out" hidden>Sign out</button>
              <button type="button" class="auth-chip btn-admin-portal" data-admin-only data-admin-portal hidden>Open Admin Portal</button>
            </div>
          </div>
          <div class="profile-detail-card">
            <h3>Share</h3>
            <p class="hint">Opens your phone/PC share sheet (WhatsApp, Instagram, etc.). Cards include download + Share &amp; Earn CTA.</p>
            <button type="button" class="auth-chip" id="btn-share-app">Share Ultimate Piano Tutor</button>
            <button type="button" class="auth-chip" id="btn-share-progress">Share my progress</button>
          </div>
        </div>
        <div class="armory-strip armory-strip-inline">
          <div class="armory-head">
            <h3>Shop</h3>
            <span class="phase-badge" id="armory-badge-profile">Open</span>
          </div>
          <div class="armory-grid">
            <article class="armory-card is-locked">
              <h3>Full Course</h3>
              <p>All lessons after the 5 free ones · exams · ads off</p>
              <button type="button" class="auth-chip" data-buy="full_course">Buy</button>
            </article>
            <article class="armory-card is-locked">
              <h3>HD Sample Pack</h3>
              <p>Real-piano samples</p>
              <button type="button" class="auth-chip" data-buy="hd_sample_pack" disabled>Coming Soon</button>
            </article>
            <article class="armory-card is-locked" id="profile-certificate-card">
              <h3>Certificate</h3>
              <p id="profile-certificate-hint">Preview sample · unlock after all classes + ${STAGE_EXAMS.length} exams</p>
              <button type="button" class="auth-chip" id="btn-certificate-profile">Open</button>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="game-screen" data-screen="gear">
      <div class="panel">
        <h2>Gear</h2>
        <h3 class="gear-section">Audio</h3>
        <div class="row">
          <button type="button" id="btn-mute" class="secondary">Mute</button>
          <button type="button" id="btn-panic" class="secondary">All Notes Off</button>
          <label>
            Volume
            <input type="range" id="vol" min="0" max="100" value="75" />
          </label>
        </div>
        <h3 class="gear-section">Input</h3>
        <div class="row">
          <label>
            Play with
            <select id="play-input">
              <option value="laptop">Laptop keys</option>
              <option value="midi">MIDI</option>
            </select>
          </label>
          <label>
            Keyboard size
            <select id="kb-size">
              <option value="auto">Auto</option>
              <option value="25">25</option>
              <option value="37">37</option>
              <option value="49">49</option>
              <option value="61" selected>61</option>
              <option value="76">76</option>
              <option value="88">88</option>
            </select>
          </label>
          <button type="button" id="btn-midi-connect" class="secondary">Connect MIDI</button>
          <button type="button" id="btn-midi-disconnect" class="secondary">Disconnect</button>
        </div>
        <p class="hint" id="kb-size-hint">Match your controller size so Practice keys line up 1:1.</p>
        <h3 class="gear-section">Status</h3>
        <div class="status-grid" id="status-grid"></div>
        <ul class="device-list" id="devices"></ul>
        <p class="hint" id="latency-hint"></p>
        <p class="hint" id="latency-detail"></p>
        <p class="hint" id="audio-backend-hint"></p>
        <p class="hint">
          Tip: wired speakers/headphones feel faster than Bluetooth. <strong>Windows</strong> software uses winmm; <strong>Mac</strong> software uses Core Audio. Android uses native AudioTrack. Browser still depends on the OS mixer.
        </p>
        <p class="hint">
          Keys: <strong>A S D F G H J K L ;</strong> · Black: <strong>W E T Y U O P</strong> · <strong>Shift</strong> = +1 octave
        </p>
        <h3 class="gear-section" id="help-section">Help</h3>
        <p class="hint">Facing a problem or have a question? Message us on WhatsApp or send an email — we'll get back to you.</p>
        <div class="help-contact">
          ${helpContactButtonsHtml()}
        </div>
        <div class="row">
          <button type="button" id="btn-tour" class="secondary">Take a Tour</button>
        </div>
        <div class="credits-plaque credits-plaque-gear" aria-label="Game credits">
          <span class="credits-plaque-kicker">LOTI Games and Apps</span>
          <strong class="credits-plaque-names">SHRIKANT &amp; SUMNNA BHARDWAJ</strong>
        </div>
      </div>
    </section>
  </div>

  <dialog id="certificate-sample-dialog" class="certificate-dialog" aria-labelledby="certificate-dialog-title">
    <div class="certificate-dialog-bar">
      <div>
        <strong id="certificate-dialog-title">Your Course Completion Certificate</strong>
        <span id="certificate-dialog-subtitle">Sample preview · final certificate is issued by the verification server</span>
      </div>
      <div class="row certificate-dialog-actions" style="margin:0">
        <button type="button" class="auth-chip" id="btn-certificate-share" hidden>Share</button>
        <button type="button" class="auth-chip" id="btn-certificate-download" hidden>Download / Print</button>
        <button type="button" class="secondary" id="btn-certificate-close">Close</button>
      </div>
    </div>
    <div class="certificate-scroll">
      <article class="certificate-sheet" aria-label="Sample Ultimate Piano Tutor certificate">
        <span class="certificate-corner corner-tl">❦</span>
        <span class="certificate-corner corner-tr">❦</span>
        <span class="certificate-corner corner-bl">❦</span>
        <span class="certificate-corner corner-br">❦</span>
        <div class="certificate-music-watermark" aria-hidden="true">𝄞　♪ ♫　♩　♪ ♫　𝄢</div>
        <div class="certificate-sample-watermark">SAMPLE · NOT VALID</div>
        <img class="certificate-logo" src="./logo.webp" alt="Ultimate Piano Tutor" />
        <p class="certificate-kicker">LOTI Games and Apps proudly presents</p>
        <h2>Course Completion Certificate</h2>
        <p class="certificate-presented">This certificate is awarded to</p>
        <div class="certificate-student">Sample Student</div>
        <p class="certificate-copy">
          for successfully completing the <strong>Ultimate Piano Tutor Course</strong>,
          all ${MAX_CURRICULUM_STAGE} stages and Gatekeeper examinations, demonstrating disciplined
          piano performance, rhythm and musicianship.
        </p>
        <div class="certificate-results">
          <span>Final Grade <strong>A</strong></span>
          <span>Accuracy <strong>94%</strong></span>
          <span>Completion Date <strong>19 July 2026</strong></span>
          <span>Certificate ID <strong>ISSUED BY SERVER</strong></span>
        </div>
        <div class="certificate-bottom">
          <div class="certificate-signature">
            <span class="signature-script">Shrikant Bhardwaj</span>
            <strong>SHRIKANT BHARDWAJ</strong>
            <small>Director</small>
          </div>
          <div class="certificate-seal">
            <svg viewBox="0 0 120 120" role="img" aria-label="Embossed Ultimate Piano Tutor seal">
              <defs>
                <radialGradient id="seal-gold" cx="35%" cy="30%">
                  <stop offset="0" stop-color="#fff1ac"/>
                  <stop offset=".48" stop-color="#e0b64f"/>
                  <stop offset="1" stop-color="#966716"/>
                </radialGradient>
                <filter id="logo-gold" color-interpolation-filters="sRGB">
                  <feColorMatrix type="matrix" values=".35 .35 .35 0 .20 .35 .35 .35 0 .12 .15 .15 .15 0 .02 0 0 0 .82 0"/>
                </filter>
                <path id="seal-ring-path" d="M60,60 m-47,0 a47,47 0 1,1 94,0 a47,47 0 1,1 -94,0"/>
              </defs>
              <circle cx="60" cy="60" r="57" fill="url(#seal-gold)" stroke="#76500f" stroke-width="3"/>
              <circle cx="60" cy="60" r="49" fill="none" stroke="#fff0a2" stroke-width="1.5"/>
              <circle cx="60" cy="60" r="38" fill="rgba(255,245,196,.24)" stroke="#8d6218"/>
              <text fill="#67450b" font-size="7.2" font-family="Georgia,serif" font-weight="700" letter-spacing="1.4">
                <textPath href="#seal-ring-path" startOffset="50%" text-anchor="middle">ULTIMATE PIANO TUTOR • VERIFIED •</textPath>
              </text>
              <image href="./logo.webp" x="25" y="33" width="70" height="42" preserveAspectRatio="xMidYMid meet" filter="url(#logo-gold)"/>
              <text x="60" y="91" text-anchor="middle" fill="#67450b" font-family="Georgia,serif" font-size="8" font-weight="700">EST. 2026</text>
            </svg>
          </div>
          <div class="certificate-signature">
            <span class="signature-script">Sumnna Bhardwaj</span>
            <strong>SUMNNA BHARDWAJ</strong>
            <small>Design &amp; Development</small>
          </div>
          <div class="certificate-qr" id="certificate-qr" aria-label="Certificate verification QR code">
            <svg viewBox="0 0 84 84" role="img" aria-label="Sample verification QR">
              <rect width="84" height="84" fill="#fff"/>
              <path fill="#071a38" d="M4 4h28v28H4zm6 6v16h16V10zm42-6h28v28H52zm6 6v16h16V10zM4 52h28v28H4zm6 6v16h16V58zm30-18h8v8h-8zm12 0h8v8h-8zm12 0h8v8h-8zM40 52h8v8h-8zm12 0h8v16h-8zm16 0h12v8H68zM40 64h8v16h-8zm24 0h8v8h-8zm12 8h4v8h-4zM52 72h16v8H52z"/>
            </svg>
            <small>Scan to Verify</small>
          </div>
        </div>
      </article>
    </div>
    <section class="certificate-rules">
      <h3>How to earn it</h3>
      <ol>
        <li>Sign in with Google (not Guest).</li>
        <li>Own the Full Course.</li>
        <li>Complete every course class.</li>
        <li>Pass all ${STAGE_EXAMS.length} Gatekeeper exams with server verification.</li>
        <li>Each exam attempt is re-graded and timed by the server.</li>
        <li>The server issues one immutable ID and unique verification QR.</li>
        <li>Download from Profile · scan QR to verify publicly.</li>
      </ol>
      <p>Guest, incomplete exams, or unverified results cannot get a certificate.</p>
    </section>
  </dialog>

  <div id="arcade-play-overlay" class="arcade-play-overlay" hidden>
    <div class="arcade-play-inner">
      <div class="arcade-hud">
        <button type="button" class="secondary" id="btn-arcade-exit">⟵ Bay</button>
        <strong id="arcade-title">Game</strong>
        <span id="arcade-score">Score 0</span>
        <span id="arcade-hi">HI 0</span>
      </div>
      <div class="arcade-canvas-wrap">
        <canvas id="arcade-canvas" tabindex="0" aria-label="Arcade game canvas"></canvas>
      </div>
      <div class="arcade-keys" id="arcade-keys" aria-label="Quick keys"></div>
      <p class="hint" id="arcade-help"></p>
    </div>
  </div>
  <div id="arcade-countdown" class="arcade-countdown" hidden aria-live="assertive"></div>
`;

const core = getPianoTutorCore();
const piano = new PianoCanvas(document.querySelector("#piano")!, {
  lowMidi: 36,
  highMidi: 96,
});
const computerKeyLabels = new Map<number, string>();
let computerLabelsShiftHeld = false;
let showingComputerKeyLabels: boolean | null = null;

function rebuildComputerKeyLabels(shiftHeld: boolean): void {
  computerKeyLabels.clear();
  for (const item of core.qwerty.getLayoutHelp(false)) {
    const midi = shiftHeld ? item.midi + 12 : item.midi;
    computerKeyLabels.set(midi, item.key);
  }
}

rebuildComputerKeyLabels(false);

function syncComputerKeyLabelOverlay(): void {
  if (!showingComputerKeyLabels || core.getNotationMode() !== "letter") {
    piano.setComputerKeyLabels(null);
    return;
  }
  rebuildComputerKeyLabels(computerLabelsShiftHeld);
  piano.setComputerKeyLabels(computerKeyLabels);
}

window.addEventListener(
  "keydown",
  (e) => {
    if (e.code !== "ShiftLeft" && e.code !== "ShiftRight") return;
    if (computerLabelsShiftHeld) return;
    computerLabelsShiftHeld = true;
    if (showingComputerKeyLabels) syncComputerKeyLabelOverlay();
  },
  true,
);
window.addEventListener(
  "keyup",
  (e) => {
    if (e.code !== "ShiftLeft" && e.code !== "ShiftRight") return;
    if (!computerLabelsShiftHeld) return;
    computerLabelsShiftHeld = false;
    if (showingComputerKeyLabels) syncComputerKeyLabelOverlay();
  },
  true,
);
window.addEventListener("blur", () => {
  if (!computerLabelsShiftHeld) return;
  computerLabelsShiftHeld = false;
  if (showingComputerKeyLabels) syncComputerKeyLabelOverlay();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "hidden" || !computerLabelsShiftHeld) return;
  computerLabelsShiftHeld = false;
  if (showingComputerKeyLabels) syncComputerKeyLabelOverlay();
});
const pianoRangeBadge = document.querySelector("#piano-range-badge")!;
const kbSizeSelect = document.querySelector<HTMLSelectElement>("#kb-size")!;
const kbSizeHint = document.querySelector("#kb-size-hint")!;
const audioBackendHint = document.querySelector("#audio-backend-hint")!;
const latencyDetail = document.querySelector("#latency-detail");

type KbMode = "auto" | KeyboardSizeId;
let kbMode: KbMode = "auto";
let kbResolved: KeyboardSizeId = 61;
let observedMidiMin = 127;
let observedMidiMax = 0;

function loadKbPref(): void {
  try {
    const raw = localStorage.getItem("pianoTutorKeyboardSize");
    if (!raw) return;
    if (raw === "auto") kbMode = "auto";
    else {
      const n = Number(raw) as KeyboardSizeId;
      if (KEYBOARD_SIZE_OPTIONS.includes(n)) {
        kbMode = n;
        kbResolved = n;
      }
    }
  } catch {
    /* */
  }
  kbSizeSelect.value = kbMode === "auto" ? "auto" : String(kbMode);
}

function saveKbPref(): void {
  try {
    localStorage.setItem("pianoTutorKeyboardSize", String(kbMode));
  } catch {
    /* */
  }
}

function resolveKeyboardSize(devices: Array<{ name: string }>): KeyboardSizeId {
  if (kbMode !== "auto") return kbMode;
  for (const d of devices) {
    const g = guessKeyboardSizeFromDeviceName(d.name);
    if (g) return g;
  }
  if (observedMidiMax >= observedMidiMin) {
    return snapObservedRangeToKeyboard(observedMidiMin, observedMidiMax);
  }
  // Laptop / unknown MIDI → comfortable 61-key window
  return 61;
}

function applyKeyboardDisplay(extraMidis: number[] = []): void {
  const status = core.getStatus();
  const devices = status.midiDevices ?? [];
  kbResolved = resolveKeyboardSize(devices);
  const base = getKeyboardRange(kbResolved);
  const covered = expandRangeToCover(base, extraMidis);
  // Prefer exact hardware footprint when guides fit inside
  let low = base.lowMidi;
  let high = base.highMidi;
  if (covered.lowMidi < base.lowMidi || covered.highMidi > base.highMidi) {
    low = covered.lowMidi;
    high = covered.highMidi;
  }
  piano.setRange(low, high);
  handsGuide.setRange(low, high);
  const span = high - low + 1;
  const label =
    span === base.highMidi - base.lowMidi + 1
      ? base.label
      : `${span}-key view · MIDI ${low}–${high}`;
  pianoRangeBadge.textContent = label;
  kbSizeHint.textContent =
    status.qwertyEnabled || status.playInput === "laptop"
      ? "Laptop mode: piano keys show A–P; ⇧ means hold Shift for the upper octave."
      : kbMode === "auto"
      ? `Auto → ${base.label}. Change below if your controller size was mis-detected.`
      : `Locked to ${base.label}. Keys scale up so the board matches your hardware.`;
}

const staffWrap = document.querySelector<HTMLElement>("#staff-wrap")!;
const staff = new StaffCanvas(document.querySelector("#staff")!);
const notationModeEl = document.querySelector<HTMLSelectElement>("#notation-mode")!;

const statusGrid = document.querySelector("#status-grid")!;
const devicesEl = document.querySelector("#devices")!;
const metroStats = document.querySelector("#metro-stats")!;
const classSessionStats = document.querySelector("#class-session-stats")!;
const pulseRow = document.querySelector("#pulse-row")!;
const logEl = document.querySelector("#log")!;
const latencyHint = document.querySelector("#latency-hint")!;
const btnMute = document.querySelector<HTMLButtonElement>("#btn-mute")!;
const btnPanic = document.querySelector<HTMLButtonElement>("#btn-panic")!;
const btnMidiConnect = document.querySelector<HTMLButtonElement>("#btn-midi-connect")!;
const btnMidiDisconnect = document.querySelector<HTMLButtonElement>("#btn-midi-disconnect")!;
const btnMetro = document.querySelector<HTMLButtonElement>("#btn-metro-toggle")!;
const btnMetroReset = document.querySelector<HTMLButtonElement>("#btn-metro-reset")!;
const vol = document.querySelector<HTMLInputElement>("#vol")!;
const playInput = document.querySelector<HTMLSelectElement>("#play-input")!;
const bpm = document.querySelector<HTMLInputElement>("#bpm")!;
const bpmVal = document.querySelector("#bpm-val")!;
const timesig = document.querySelector<HTMLSelectElement>("#timesig")!;
const metroVol = document.querySelector<HTMLInputElement>("#metro-vol")!;
const lockGrade = document.querySelector<HTMLInputElement>("#lock-grade")!;
const pieceSelect = document.querySelector<HTMLSelectElement>("#piece-select")!;
const btnPiecePlay = document.querySelector<HTMLButtonElement>("#btn-piece-play")!;
const btnPieceLearn = document.querySelector<HTMLButtonElement>("#btn-piece-learn")!;
const btnPiecePerform =
  document.querySelector<HTMLButtonElement>("#btn-piece-perform")!;
const btnPieceStop = document.querySelector<HTMLButtonElement>("#btn-piece-stop")!;
const pieceMeta = document.querySelector("#piece-meta")!;
const pieceNotes = document.querySelector("#piece-notes")!;
const practicePackSelect =
  document.querySelector<HTMLSelectElement>("#practice-pack-select")!;
const btnPracticePack =
  document.querySelector<HTMLButtonElement>("#btn-practice-pack")!;
const practicePackMeta = document.querySelector("#practice-pack-meta")!;

const libraryPieces = listPieces();
function renderPieceOptions(pieces: MusicPiece[]): void {
  pieceSelect.innerHTML = pieces
    .map(
      (p) =>
        `<option value="${p.id}">[${p.category}] ${p.title} · Stage ${p.stage} · ${p.bpm} BPM</option>`,
    )
    .join("");
}
renderPieceOptions(libraryPieces);

practicePackSelect.innerHTML = [
  `<option value="all">All Library Pieces</option>`,
  ...PRACTICE_PACKS.map((p) => `<option value="${p.id}">${p.title}</option>`),
].join("");

function refreshPracticePackMeta(): void {
  const pack = PRACTICE_PACKS.find((p) => p.id === practicePackSelect.value);
  practicePackMeta.textContent = pack
    ? `${pack.description} · ${pack.pieceIds.length} pieces`
    : `${libraryPieces.length} built-in pieces`;
}

practicePackSelect.addEventListener("change", refreshPracticePackMeta);
btnPracticePack.addEventListener("click", () => {
  const pack = PRACTICE_PACKS.find((p) => p.id === practicePackSelect.value);
  const pieces = pack
    ? pack.pieceIds
        .map((id) => libraryPieces.find((piece) => piece.id === id))
        .filter((piece): piece is MusicPiece => !!piece)
    : libraryPieces;
  renderPieceOptions(pieces);
  refreshPieceMeta();
  pieceSelect.focus({ preventScroll: true });
});
refreshPracticePackMeta();

function showPieceMeta(piece: MusicPiece): void {
  const mode = core.getNotationMode();
  pieceMeta.textContent = `${piece.key} · ${piece.timeSignature} · ${piece.hand} hand — ${piece.description}`;
  pieceNotes.innerHTML = piece.notes
    .slice(0, 24)
    .map((n) => {
      const label = labelForMidi(n.midi, mode, {
        finger: n.finger ?? defaultCPositionFinger(n.midi),
      });
      return `<div>beat ${n.beat.toFixed(1)}  ${label}  dur=${n.duration}${n.finger ? `  finger ${n.finger}` : ""}</div>`;
    })
    .join("");
  if (piece.notes.length > 24) {
    pieceNotes.innerHTML += `<div>… +${piece.notes.length - 24} more events</div>`;
  }
  piano.setGuideNotes(piece.notes.slice(0, 16).map((n) => n.midi));
  staff.setNotes(piece.notes.slice(0, 12).map((n) => n.midi));
}

function refreshPieceMeta(): void {
  const piece = libraryPieces.find((p) => p.id === pieceSelect.value);
  if (piece) showPieceMeta(piece);
}

pieceSelect.addEventListener("change", refreshPieceMeta);
refreshPieceMeta();

notationModeEl.addEventListener("change", () => {
  const mode = notationModeEl.value as NotationMode;
  core.setNotationMode(mode);
  piano.setNotationMode(mode);
  staffWrap.hidden = mode !== "staff";
  if (mode === "staff") staff.resize();
  // Laptop key overlay only for Letters mode — other notations must show through.
  if (mode === "letter" && showingComputerKeyLabels) syncComputerKeyLabelOverlay();
  else piano.setComputerKeyLabels(null);
  piano.draw();
  refreshPieceMeta();
});

btnPiecePlay.addEventListener("click", async () => {
  void enterMode("play");
  await core.ensureAudio();
  await core.playLibraryPiece(pieceSelect.value);
});

btnPieceLearn.addEventListener("click", async () => {
  await core.startLibraryPractice(pieceSelect.value, "learn");
  void enterMode("play");
});

btnPiecePerform.addEventListener("click", async () => {
  await core.startLibraryPractice(pieceSelect.value, "performance");
  void enterMode("play");
});

btnPieceStop.addEventListener("click", () => {
  core.stopImportedPractice();
  core.stopLibraryPiece();
});

const midiDrop = document.querySelector<HTMLDivElement>("#midi-drop")!;
const midiFile = document.querySelector<HTMLInputElement>("#midi-file")!;
const btnImportPlay = document.querySelector<HTMLButtonElement>("#btn-import-play")!;
const btnImportLearn = document.querySelector<HTMLButtonElement>("#btn-import-learn")!;
const btnImportPerform = document.querySelector<HTMLButtonElement>("#btn-import-perform")!;
const btnImportStop = document.querySelector<HTMLButtonElement>("#btn-import-stop")!;
const btnImportClear = document.querySelector<HTMLButtonElement>("#btn-import-clear")!;
const importMeta = document.querySelector("#import-meta")!;

function setImportButtons(ready: boolean, practicing: boolean): void {
  btnImportPlay.disabled = !ready || practicing;
  btnImportLearn.disabled = !ready || practicing;
  btnImportPerform.disabled = !ready || practicing;
  btnImportStop.disabled = !practicing;
  btnImportClear.disabled = !ready;
}

async function handleMidiFile(file: File): Promise<void> {
  const buf = await file.arrayBuffer();
  const title = file.name.replace(/\.(mid|midi)$/i, "");
  const piece = core.importMidiFile(buf, { title });
  const parsed = core.getLastParsedMidi();
  importMeta.textContent = `${piece.title}: ${piece.notes.length} notes · ${piece.bpm} BPM · ${piece.timeSignature} · hand=${piece.hand} · tracks=${parsed?.trackCount ?? "?"}`;
  setImportButtons(true, false);
  try {
    await saveMidiFile(buf.slice(0), {
      title: piece.title,
      noteCount: piece.notes.length,
      bpm: piece.bpm,
      timeSignature: piece.timeSignature,
    });
    await refreshMidiLibraryList();
  } catch {
    importMeta.textContent += " · (could not save to device library)";
  }
}

const midiLibraryList = document.querySelector<HTMLUListElement>("#midi-library-list")!;
const midiLibraryEmpty = document.querySelector<HTMLElement>("#midi-library-empty")!;

async function refreshMidiLibraryList(): Promise<void> {
  const items = await listSavedMidis();
  midiLibraryEmpty.hidden = items.length > 0;
  midiLibraryList.innerHTML = items
    .map(
      (m) => `
      <li class="midi-library-item" data-midi-id="${m.id}">
        <div class="midi-library-info">
          <strong>${escapeHtml(m.title)}</strong>
          <span>${m.noteCount} notes · ${m.bpm} BPM · ${escapeHtml(m.timeSignature)}</span>
        </div>
        <div class="midi-library-actions">
          <button type="button" class="auth-chip" data-midi-load="${m.id}">Open</button>
          <button type="button" class="secondary" data-midi-del="${m.id}">Delete</button>
        </div>
      </li>`,
    )
    .join("");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

midiLibraryList.addEventListener("click", (e) => {
  const t = e.target as HTMLElement;
  const loadId = t.closest<HTMLElement>("[data-midi-load]")?.dataset.midiLoad;
  const delId = t.closest<HTMLElement>("[data-midi-del]")?.dataset.midiDel;
  if (loadId) {
    void (async () => {
      const entry = await getSavedMidi(loadId);
      if (!entry) return;
      const piece = core.importMidiFile(entry.bytes, { title: entry.title });
      const parsed = core.getLastParsedMidi();
      importMeta.textContent = `Loaded “${piece.title}”: ${piece.notes.length} notes · ${piece.bpm} BPM · tracks=${parsed?.trackCount ?? "?"}`;
      setImportButtons(true, false);
    })();
  }
  if (delId) {
    void (async () => {
      await deleteSavedMidi(delId);
      if (core.getImportedPiece()?.title) {
        /* keep current in-memory piece */
      }
      await refreshMidiLibraryList();
    })();
  }
});

void refreshMidiLibraryList();

midiFile.addEventListener("change", () => {
  const f = midiFile.files?.[0];
  if (f) void handleMidiFile(f);
});

["dragenter", "dragover"].forEach((evt) => {
  midiDrop.addEventListener(evt, (e) => {
    e.preventDefault();
    midiDrop.classList.add("dragover");
  });
});
["dragleave", "drop"].forEach((evt) => {
  midiDrop.addEventListener(evt, (e) => {
    e.preventDefault();
    midiDrop.classList.remove("dragover");
  });
});
midiDrop.addEventListener("drop", (e) => {
  const f = e.dataTransfer?.files?.[0];
  if (f) void handleMidiFile(f);
});

btnImportPlay.addEventListener("click", async () => {
  void enterMode("play");
  await core.ensureAudio();
  await core.playImportedPiece();
});

async function startImportedMode(mode: ImportedPracticeMode): Promise<void> {
  try {
    await core.startImportedPractice(mode);
    setImportButtons(true, true);
    void enterMode("play");
    importMeta.textContent =
      mode === "learn"
        ? "Learn mode — wait for green keys; wrong notes pause progress."
        : "Performance mode — keep time; misses auto-advance. Metronome sync is on.";
  } catch (e) {
    comboBanner.textContent = e instanceof Error ? e.message : "Could not start imported practice";
  }
}

btnImportLearn.addEventListener("click", () => {
  void startImportedMode("learn");
});
btnImportPerform.addEventListener("click", () => {
  void startImportedMode("performance");
});
btnImportStop.addEventListener("click", () => {
  core.stopImportedPractice();
  setImportButtons(!!core.getImportedPiece(), false);
  importMeta.textContent = core.getImportedPiece()
    ? "Practice stopped. Choose Learn or Performance again."
    : "No file yet.";
});
btnImportClear.addEventListener("click", () => {
  core.clearImportedPiece();
  importMeta.textContent = "No file yet. Learn waits for the correct key; Performance keeps time and grades hits/misses.";
  setImportButtons(false, false);
  midiFile.value = "";
});

const stageSelect = document.querySelector<HTMLSelectElement>("#stage-select")!;
const classSelect = document.querySelector<HTMLSelectElement>("#class-select")!;
const classMins = document.querySelector<HTMLInputElement>("#class-mins")!;
const classMinsVal = document.querySelector("#class-mins-val")!;
const classDesc = document.querySelector("#class-desc")!;
const classPlanMeta = document.querySelector("#class-plan-meta")!;
const btnClassStart = document.querySelector<HTMLButtonElement>("#btn-class-start")!;
const btnClassStop = document.querySelector<HTMLButtonElement>("#btn-class-stop")!;
const btnExamStart = document.querySelector<HTMLButtonElement>("#btn-exam-start")!;
const btnExamStop = document.querySelector<HTMLButtonElement>("#btn-exam-stop")!;
const expectedHint = document.querySelector("#expected-hint")!;
const coachPanel = document.querySelector<HTMLElement>("#coach-panel")!;
const coachTitle = document.querySelector("#coach-title")!;
const coachBullets = document.querySelector("#coach-bullets")!;
const btnCoachNext = document.querySelector<HTMLButtonElement>("#btn-coach-next")!;
const btnCoachSkip = document.querySelector<HTMLButtonElement>("#btn-coach-skip")!;
const coachAnim = new CoachCanvas(document.querySelector("#coach-canvas")!);

const fx = new GameFxBackdrop(document.querySelector("#fx-bg")!);
fx.start();

const gameShell = document.querySelector<HTMLElement>("#game-shell")!;
const comboBanner = document.querySelector("#combo-banner")!;
const hudStage = document.querySelector("#hud-stage")!;
const hudRank = document.querySelector("#hud-rank")!;
const hudXp = document.querySelector("#hud-xp")!;
const hudClears = document.querySelector("#hud-clears")!;
const xpFill = document.querySelector<HTMLElement>("#xp-fill")!;
const topRank = document.querySelector("#top-rank")!;
const profileName = document.querySelector<HTMLInputElement>("#profile-name")!;
const profileAvatar = document.querySelector<HTMLImageElement>("#profile-avatar")!;
const avatarFile = document.querySelector<HTMLInputElement>("#avatar-file")!;
const hitFx = new HitParticleLayer(document.querySelector("#hit-fx")!);
const handsGuide = new HandsGuideOverlay(document.querySelector("#hands-guide")!);
const levelMap = new LevelMapCanvas(document.querySelector("#level-map")!);
const results = new ResultsOverlay(document.body);

loadKbPref();
applyKeyboardDisplay();
kbSizeSelect.addEventListener("change", () => {
  const v = kbSizeSelect.value;
  if (v === "auto") kbMode = "auto";
  else kbMode = Number(v) as KeyboardSizeId;
  saveKbPref();
  applyKeyboardDisplay();
  sfx.play("click");
});

async function ensureSfx(): Promise<void> {
  await core.ensureAudio();
  const ctx = core.synth.audioContext;
  if (ctx) await sfx.unlock(ctx);
}

const arcadeHost = new ArcadeHost(
  document.querySelector<HTMLElement>('[data-screen="arcade"]')!,
  core,
  results,
  ensureSfx,
);

(() => {
  const keys = document.querySelector("#arcade-keys");
  if (!keys) return;
  const midis = [48, 50, 52, 53, 55, 60, 62, 64, 65, 67, 69, 71, 72];
  const labels = ["C3", "D3", "E3", "F3", "G3", "C4", "D", "E", "F", "G", "A", "B", "C5"];
  keys.innerHTML = midis
    .map(
      (m, i) =>
        `<button type="button" class="arcade-key" data-midi="${m}">${labels[i]}</button>`,
    )
    .join("");
  keys.querySelectorAll<HTMLButtonElement>(".arcade-key").forEach((btn) => {
    const midi = Number(btn.dataset.midi);
    const down = () => {
      void ensureSfx();
      core.playNoteImmediate(midi, 100);
    };
    const up = () => core.releaseNote(midi);
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      down();
    });
    btn.addEventListener("pointerup", up);
    btn.addEventListener("pointerleave", up);
  });
})();

let hitStreak = 0;
let lastFeedbackSig = "";
let lastExamPhase = "";
let lastClassPhase = "";
let lastImportedCompleted = false;
let importedPerfRaf = 0;
let lastXpShown = -1;
let lastProgressSig = "";
let verifiedCertificate: VerifiedCertificate | null = null;
let verifiedCertificateOwner = "";
let certificateRequestBusy = false;
let verifiedExamSessionId: string | null = null;
let verifiedExamSubmitBusy = false;
let pendingClassStart: { id: string; mins: number } | null = null;
let lastCompletedClassContext: { id: string; mins: number; stageId: number } | null =
  null;
let coachQueue: CoachLesson[] = [];
let coachIndex = 0;

type PlayerProfile = { name: string; photo: string | null };
function loadProfile(): PlayerProfile {
  try {
    const raw = localStorage.getItem("pianoTutorProfile");
    if (raw) return JSON.parse(raw) as PlayerProfile;
  } catch {
    /* ignore */
  }
  return { name: "PLAYER ONE", photo: null };
}
function saveProfile(p: PlayerProfile): void {
  localStorage.setItem("pianoTutorProfile", JSON.stringify(p));
}
function defaultAvatarDataUrl(name: string): string {
  const c = document.createElement("canvas");
  c.width = 168;
  c.height = 168;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 168, 168);
  g.addColorStop(0, "#1a7fd4");
  g.addColorStop(1, "#0a1e3d");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 168, 168);
  ctx.fillStyle = "#8ad6ff";
  ctx.font = "700 56px Oxanium, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
  ctx.fillText(initials || "P1", 84, 90);
  return c.toDataURL("image/png");
}
let profile = loadProfile();
profileName.value = profile.name;
profileAvatar.src = profile.photo || defaultAvatarDataUrl(profile.name);

profileName.addEventListener("change", () => {
  // Account sync is handled in bindAccountUi; keep local cache in sync only.
  profile.name = profileName.value.trim() || "PLAYER ONE";
  if (!profile.photo || profile.photo.startsWith("data:image/png")) {
    /* keep */
  }
  saveProfile(profile);
});
document.querySelector("#btn-avatar")!.addEventListener("click", () => avatarFile.click());
avatarFile.addEventListener("change", () => {
  const f = avatarFile.files?.[0];
  if (!f) return;
  void (async () => {
    try {
      const dataUrl = await compressAvatarFile(f);
      profile.photo = dataUrl;
      profileAvatar.src = dataUrl;
      const editAvatar = document.querySelector<HTMLImageElement>("#profile-edit-avatar");
      if (editAvatar) editAvatar.src = dataUrl;
      saveProfile(profile);
      const snap = getAccountSnapshot();
      if (snap.user && !snap.isAnonymous) {
        await saveAccountPhoto(dataUrl);
        comboBanner.textContent = "Photo updated.";
      } else {
        comboBanner.textContent = "Photo saved on this device.";
      }
    } catch (e) {
      comboBanner.textContent = e instanceof Error ? e.message : "Photo update failed";
    } finally {
      avatarFile.value = "";
    }
  })();
});

// Seed Profile-tab avatar immediately (hub already has src).
{
  const editAvatar = document.querySelector<HTMLImageElement>("#profile-edit-avatar");
  const editName = document.querySelector<HTMLInputElement>("#profile-edit-name");
  if (editAvatar) {
    editAvatar.referrerPolicy = "no-referrer";
    editAvatar.src = profile.photo || defaultAvatarDataUrl(profile.name);
  }
  if (editName) editName.value = profile.name;
}

let applyPremiumCampaignUnlock: (() => void) | null = null;

void bindAccountUi({
  profileName,
  profileAvatar,
  getLocal: () => profile,
  setLocal: (p) => {
    profile = p;
    saveProfile(profile);
  },
  defaultAvatar: defaultAvatarDataUrl,
  onToast: (msg) => {
    comboBanner.textContent = msg;
  },
  onPremiumUnlock: () => {
    applyPremiumCampaignUnlock?.();
  },
  onAccountChange: (snap) => {
    // Defer until main.ts finishes declaring certificate DOM bindings (avoids TDZ crash).
    queueMicrotask(() => {
      try {
        if (verifiedCertificateOwner && verifiedCertificateOwner !== snap.user?.uid) {
          verifiedCertificate = null;
          verifiedCertificateOwner = "";
        }
        const ownsPremium = !snap.isAnonymous && snap.plan === "full_course";
        configureAdsForAccount(ownsPremium);
        const rewardCard = document.querySelector<HTMLElement>("#rewarded-ad-card");
        if (rewardCard) rewardCard.hidden = ownsPremium || !areAdsEnabled();
        refreshCertificateUi();
        refreshClassOptions();
        void refreshStreakBar();
      } catch (e) {
        console.warn("[account] onAccountChange", e);
      }
    });
  },
});
setAdSurface("hub");

document.querySelector("#btn-rewarded-ad")?.addEventListener("click", () => {
  const btn = document.querySelector<HTMLButtonElement>("#btn-rewarded-ad");
  if (btn) btn.disabled = true;
  void showRewardedAdFreeBreak().then((granted) => {
    comboBanner.textContent = granted
      ? "Reward earned — ads paused for 30 minutes."
      : "Rewarded ad was not completed. Please try again later.";
    if (btn) btn.disabled = false;
  });
});

const certificateDialog =
  document.querySelector<HTMLDialogElement>("#certificate-sample-dialog");
const certificateSubtitle = document.querySelector("#certificate-dialog-subtitle");
const certificateStudent = document.querySelector(".certificate-student");
const certificateSampleMark = document.querySelector(".certificate-sample-watermark");
const btnCertificateDownload = document.querySelector<HTMLButtonElement>("#btn-certificate-download");
const btnCertificateShare = document.querySelector<HTMLButtonElement>("#btn-certificate-share");
const profileCertificateHint = document.querySelector("#profile-certificate-hint");
const certificateQr = document.querySelector<HTMLElement>("#certificate-qr");
const certificateResults = certificateDialog?.querySelector<HTMLElement>(".certificate-results");
const sampleCertificateResultsHtml = certificateResults?.innerHTML ?? "";
const sampleCertificateQrHtml = certificateQr?.innerHTML ?? "";

function refreshCertificateUi(): void {
  const snap = getAccountSnapshot();
  const progress = core.getProgress();
  const elig = evaluateCertificateEligibility({
    displayName: snap.displayName || profileName.value || "Piano Student",
    isSignedIn: !!snap.user,
    isAnonymous: !!snap.isAnonymous,
    plan: snap.plan,
    progress,
  });
  const certificate = verifiedCertificate;

  if (certificateStudent) {
    certificateStudent.textContent = certificate?.studentName ?? "Sample Student";
  }
  if (certificateSampleMark) {
    (certificateSampleMark as HTMLElement).hidden = !!certificate;
    certificateSampleMark.textContent = certificate ? "" : "SAMPLE · NOT VALID";
  }
  if (certificateSubtitle) {
    certificateSubtitle.textContent = certificate
      ? `Server verified · ${certificate.id}`
      : certificateRequestBusy
        ? "Checking server-verified exam records…"
        : elig.eligible
          ? "Course complete locally · server verification required"
          : `Sample preview · complete all classes + all ${STAGE_EXAMS.length} exams`;
  }
  if (btnCertificateDownload) btnCertificateDownload.hidden = !certificate;
  if (btnCertificateShare) btnCertificateShare.hidden = !certificate;

  if (certificateResults && certificate) {
    const issueDate = new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(certificate.issuedAtIso));
    certificateResults.innerHTML = `
      <span>Server Status <strong>VERIFIED</strong></span>
      <span>Accuracy <strong>${certificate.accuracyPct == null ? "—" : `${certificate.accuracyPct.toFixed(1)}%`}</strong></span>
      <span>Completion Date <strong>${escapeHtml(issueDate)}</strong></span>
      <span>Certificate ID <strong>${escapeHtml(certificate.id)}</strong></span>
    `;
  } else if (certificateResults) {
    certificateResults.innerHTML = sampleCertificateResultsHtml;
  }
  if (!certificate && certificateQr) {
    certificateQr.innerHTML = sampleCertificateQrHtml;
  }

  if (profileCertificateHint) {
    profileCertificateHint.textContent = certificate
      ? "Server-verified certificate ready — download from Preview"
      : elig.eligible
        ? "Open to verify exams and issue your unique certificate"
      : elig.reasons[0] ?? "Preview sample";
  }

  const shopCard = document.querySelector("#btn-certificate-sample");
  if (shopCard) shopCard.textContent = certificate ? "Your Certificate" : "Preview";
}

async function requestVerifiedCertificate(): Promise<void> {
  if (certificateRequestBusy || verifiedCertificate) return;
  const snap = getAccountSnapshot();
  const elig = evaluateCertificateEligibility({
    displayName: snap.displayName || profileName.value || "Piano Student",
    isSignedIn: !!snap.user,
    isAnonymous: !!snap.isAnonymous,
    plan: snap.plan,
    progress: core.getProgress(),
  });
  if (!elig.eligible || !snap.user) return;
  certificateRequestBusy = true;
  refreshCertificateUi();
  try {
    const certificate = await issueCourseCertificate();
    verifiedCertificate = certificate;
    verifiedCertificateOwner = snap.user.uid;
    if (certificateQr) {
      const dataUrl = await QRCode.toDataURL(certificate.verificationUrl, {
        width: 180,
        margin: 1,
        errorCorrectionLevel: "M",
        color: { dark: "#071a38", light: "#ffffff" },
      });
      certificateQr.innerHTML = `
        <img src="${dataUrl}" alt="QR code for certificate ${escapeHtml(certificate.id)}" />
        <small>Scan to Verify</small>
      `;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "Server verification failed.";
    comboBanner.textContent = message;
    if (profileCertificateHint) profileCertificateHint.textContent = message;
  } finally {
    certificateRequestBusy = false;
    refreshCertificateUi();
  }
}

function openCertificateDialog(): void {
  refreshCertificateUi();
  certificateDialog?.showModal();
  void requestVerifiedCertificate();
}

document.querySelector("#btn-certificate-sample")?.addEventListener("click", () => {
  openCertificateDialog();
});
document.querySelector("#btn-certificate-profile")?.addEventListener("click", () => {
  openCertificateDialog();
});
document.querySelector("#btn-certificate-close")?.addEventListener("click", () => {
  certificateDialog?.close();
});
btnCertificateDownload?.addEventListener("click", () => {
  const sheet = certificateDialog?.querySelector<HTMLElement>(".certificate-sheet");
  if (sheet) printCertificateSheet(sheet, "Ultimate Piano Tutor Certificate");
});
btnCertificateShare?.addEventListener("click", () => {
  const certificate = verifiedCertificate;
  if (!certificate) return;
  btnCertificateShare.disabled = true;
  btnCertificateShare.textContent = "Sharing…";
  void shareCertificateCard({
    studentName: certificate.studentName,
    certificateId: certificate.id,
    course: certificate.course,
    issuedAtIso: certificate.issuedAtIso,
    accuracyPct: certificate.accuracyPct,
    examsVerified: certificate.examsVerified,
    verificationUrl: certificate.verificationUrl,
  })
    .then((how) => {
      comboBanner.textContent =
        how === "shared"
          ? "Certificate shared."
          : how === "downloaded"
            ? "Certificate image saved — post it anywhere."
            : "Certificate caption copied.";
    })
    .catch((e) => {
      if (e instanceof DOMException && e.name === "AbortError") return;
      comboBanner.textContent = e instanceof Error ? e.message : "Share failed";
    })
    .finally(() => {
      btnCertificateShare.disabled = false;
      btnCertificateShare.textContent = "Share";
    });
});
certificateDialog?.addEventListener("click", (event) => {
  if (event.target === certificateDialog) certificateDialog.close();
});

document.querySelector("#btn-share-app")?.addEventListener("click", () => {
  const snap = getAccountSnapshot();
  const name = snap.displayName || profileName.value || "Piano Student";
  void shareAppInvite(name)
    .then((how) => {
      comboBanner.textContent =
        how === "shared"
          ? "Shared! Opens WhatsApp / Instagram / etc. — download + earn CTA included."
          : how === "copied"
            ? "Invite copied — paste in any social app."
            : "Invite ready.";
    })
    .catch((e) => {
      if (e instanceof DOMException && e.name === "AbortError") return;
      comboBanner.textContent = e instanceof Error ? e.message : "Share failed";
    });
});

document.querySelector("#btn-share-progress")?.addEventListener("click", () => {
  const progress = core.getProgress();
  const snap = getAccountSnapshot();
  const name = snap.displayName || profileName.value || "Piano Student";
  const cls = core.getCurriculumClass(classSelect.value);
  void shareProgressCard({
    studentName: name,
    unlockedStage: progress.unlockedStage,
    classesCompleted: progress.completedClassIds.length,
    classesTotal: core.listCurriculumClasses().length,
    examsPassed: progress.passedExamIds.length,
    examsTotal: STAGE_EXAMS.length,
    currentClassTitle: cls ? `${cls.code} ${cls.title}` : undefined,
  })
    .then((how) => {
      comboBanner.textContent =
        how === "shared"
          ? "Progress card shared with download + Share & Earn CTA."
          : how === "downloaded"
            ? "Progress card saved — share the PNG anywhere."
            : "Progress text copied.";
    })
    .catch((e) => {
      if (e instanceof DOMException && e.name === "AbortError") return;
      comboBanner.textContent = e instanceof Error ? e.message : "Share failed";
    });
});

consumeInviteParam((from) => {
  comboBanner.textContent = `Welcome — invited by ${from}`;
});

void import("./native/playInstallReferrer").then(({ capturePlayInstallReferrerOnce }) =>
  capturePlayInstallReferrerOnce().then((code) => {
    if (code) {
      comboBanner.textContent = `Referral from Play Store (${code}) — sign in to link it.`;
    }
  }),
);

const refCode = consumeReferralParam();
if (refCode) {
  comboBanner.textContent = `Referral locked in (${refCode}) — buy Full Course to reward your friend.`;
}

const shareEarnRoot = document.querySelector<HTMLElement>("#share-earn-root");
if (shareEarnRoot) {
  bindShareEarnUi({
    root: shareEarnRoot,
    onToast: (msg) => {
      comboBanner.textContent = msg;
    },
  });
}

function setGameMode(mode: string): void {
  const prev = gameShell.dataset.mode;
  gameShell.dataset.mode = mode;
  document.querySelectorAll(".mode-btn").forEach((b) => {
    b.classList.toggle("is-active", (b as HTMLElement).dataset.mode === mode);
  });
  document.querySelectorAll(".game-screen").forEach((s) => {
    s.classList.toggle("is-active", (s as HTMLElement).dataset.screen === mode);
  });
  if (mode === "play") {
    requestAnimationFrame(() => {
      piano.resize();
      hitFx.resize();
      handsGuide.resize();
    });
  }
  if (mode === "campaign") {
    requestAnimationFrame(() => {
      levelMap.resize();
      refreshLevelMap();
      if (!coachPanel.hidden) coachAnim.resize();
    });
  }
  if (mode === "arcade") {
    requestAnimationFrame(() => arcadeHost.resize());
  }
  setAdSurface(mode === "home" ? "hub" : "content");
  // Natural-break interstitial: helper applies cooldown, every-second-break,
  // three-minute warmup, daily cap, temporary-ad-free and premium rules.
  if (mode === "home" && (prev === "play" || prev === "campaign" || prev === "arcade")) {
    void maybeShowNaturalInterstitial();
  }
}

/** Reveal the next interaction after an explicit user action, without animation jank. */
function focusScreen(el: HTMLElement | null, opts?: { select?: boolean }): void {
  if (!el) return;
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  try {
    el.focus({ preventScroll: true });
  } catch {
    /* ignore */
  }
  el.classList.add("is-screen-focus");
  window.setTimeout(() => el.classList.remove("is-screen-focus"), 900);
  if (opts?.select) {
    const btn = el.querySelector<HTMLButtonElement>("button:not([hidden])");
    btn?.focus({ preventScroll: true });
  }
  // Wait for hidden panels/canvases to finish layout, then scroll only when
  // the target is actually outside the useful viewport.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const topSafe = 72;
      const bottomSafe = window.innerHeight - 24;
      if (rect.top < topSafe || rect.bottom > bottomSafe) {
        const tall = rect.height > window.innerHeight * 0.7;
        el.scrollIntoView({
          behavior: "auto",
          block: tall ? "start" : "center",
          inline: "nearest",
        });
      }
    });
  });
}

const modeTransition = document.querySelector<HTMLElement>("#mode-transition")!;
const mtLabel = document.querySelector("#mt-label")!;
let transitionBusy = false;

const MODE_LABELS: Record<string, string> = {
  home: "HUB",
  campaign: "CAMPAIGN",
  play: "PRACTICE",
  arcade: "ARCADE",
  vault: "VAULT",
  profile: "PROFILE",
  gear: "GEAR",
};

async function enterMode(mode: string, withFx = true): Promise<void> {
  if (transitionBusy) return;
  const prev = gameShell.dataset.mode;
  if (
    prev === "play" &&
    mode !== "play" &&
    mode !== "campaign" &&
    core.getStatus().classSession.phase === "running"
  ) {
    core.stopClassSession();
  }
  if (!withFx || prev === mode) {
    setGameMode(mode);
    window.scrollTo({ top: 0, behavior: "auto" });
    return;
  }
  transitionBusy = true;
  void ensureSfx().then(() => sfx.play("transition"));
  mtLabel.textContent = MODE_LABELS[mode] ?? mode.toUpperCase();
  modeTransition.hidden = false;
  modeTransition.classList.remove("is-on");
  void modeTransition.offsetWidth;
  modeTransition.classList.add("is-on");
  await new Promise((r) => setTimeout(r, 120));
  if (mode !== "arcade") arcadeHost.exitGame();
  setGameMode(mode);
  window.scrollTo({ top: 0, behavior: "auto" });
  await new Promise((r) => setTimeout(r, 220));
  modeTransition.classList.remove("is-on");
  modeTransition.hidden = true;
  transitionBusy = false;
}

document.querySelectorAll(".mode-btn").forEach((btn) => {
  btn.addEventListener("pointerenter", () => sfx.play("hover"));
  btn.addEventListener("click", () => {
    sfx.play("click");
    const mode = (btn as HTMLElement).dataset.mode;
    if (mode) void enterMode(mode, mode !== "home");
  });
});
document.querySelectorAll("[data-portal]").forEach((btn) => {
  btn.addEventListener("pointerenter", () => sfx.play("hover"));
  btn.addEventListener("click", () => {
    sfx.play("confirm");
    const mode = (btn as HTMLElement).dataset.portal;
    if (mode) {
      void enterMode(mode, true).then(() => {
        if ((btn as HTMLElement).id === "btn-hub-earn" || mode === "profile") {
          if ((btn as HTMLElement).id === "btn-hub-earn") {
            window.setTimeout(() => focusShareEarnPanel(), 320);
          }
        }
      });
    }
  });
});
document.querySelector("#btn-home")!.addEventListener("click", () => {
  sfx.play("click");
  void enterMode("home", true);
});

function showCoachLesson(lesson: CoachLesson): void {
  setGameMode("campaign");
  coachPanel.hidden = false;
  coachTitle.textContent = lesson.title;
  coachBullets.innerHTML = lesson.bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join("");
  coachAnim.resize();
  coachAnim.show(lesson);
  btnCoachNext.textContent =
    coachIndex >= coachQueue.length - 1 ? "Begin practice" : "Next tip";
  focusScreen(coachPanel, { select: true });
}

function hideCoach(): void {
  coachPanel.hidden = true;
  coachAnim.stop();
  coachQueue = [];
  coachIndex = 0;
}

function beginPendingClass(): void {
  hideCoach();
  hideExamBrief();
  if (!pendingClassStart) return;
  const { id, mins } = pendingClassStart;
  pendingClassStart = null;
  core.prepareClassSession(id, mins);
  void core.startClassSession();
  notationModeEl.value = "finger";
  core.setNotationMode("finger");
  piano.setNotationMode("finger");
  staffWrap.hidden = true;
  setGameMode("play");
  comboBanner.textContent = "CLASS LIVE — exact keys only · finger numbers on glowing keys";
  comboBanner.classList.add("is-hot");
  focusScreen(document.querySelector<HTMLElement>("#class-mission-bar"));
}

function ownsFullCourse(): boolean {
  const snap = getAccountSnapshot();
  return !snap.isAnonymous && snap.plan === "full_course";
}

function canStartClass(classId: string): boolean {
  return ownsFullCourse() || isClassInFreeTrial(stages, classId);
}

function showFreeTrialPaywall(reason: "lesson" | "exam" | "trial-done"): void {
  const body =
    reason === "exam"
      ? `Gatekeeper exams are part of the Full Course. The free trial covers the first ${FREE_TRIAL_LESSON_COUNT} lessons only.`
      : reason === "trial-done"
        ? `You've finished all ${FREE_TRIAL_LESSON_COUNT} free lessons. Buy the Full Course to keep going — every lesson, exams, certificate, no ads.`
        : `This lesson is part of the Full Course. The free trial covers the first ${FREE_TRIAL_LESSON_COUNT} lessons only.`;
  showEngagementModal({
    kind: "buy",
    title: reason === "trial-done" ? "Free trial complete 🎉" : "Unlock the Full Course",
    body,
    primaryLabel: "Buy Full Course",
    hideNever: true,
    onPrimary: () => {
      void enterMode("home", true);
      document.querySelector<HTMLButtonElement>('[data-buy="full_course"]')?.click();
    },
    onLater: () => {},
    onNever: () => {},
  });
}

function startClassWithCoach(id: string, mins: number): void {
  if (!canStartClass(id)) {
    showFreeTrialPaywall("lesson");
    sfx.play("miss");
    return;
  }
  pendingClassStart = { id, mins };
  // In-progress class stays focused if user leaves mid-session / reopens later.
  const tutor = getClassById(id);
  if (tutor) {
    saveLastCampaign({ stageId: tutor.stage, classId: id, minutes: mins });
  }
  coachQueue = getCoachLessonsForClass(id);
  if (coachQueue.length === 0) {
    const tutorClass = core.getCurriculumClass(id);
    if (tutorClass) {
      const scene: CoachLesson["scene"] =
        tutorClass.kind === "chord"
          ? "chord-shape"
          : tutorClass.kind === "scale" || tutorClass.kind === "speed"
            ? "thumb-tuck"
            : tutorClass.kind === "blind" || tutorClass.kind === "ear"
              ? "blind-focus"
              : "posture";
      coachQueue = [{
        id: `class-briefing-${id}`,
        title: `${tutorClass.code} · ${tutorClass.title}`,
        stages: [tutorClass.stage],
        scene,
        bullets: [
          tutorClass.description,
          tutorClass.skills?.length
            ? `Focus: ${tutorClass.skills.join(", ")}.`
            : "Keep the hand relaxed and prioritise clean, even notes.",
          `Session: ${mins} minutes · ${tutorClass.assessment ?? "guided"} mode.`,
        ],
        durationSec: 10,
      }];
    }
  }
  coachIndex = 0;
  if (coachQueue.length === 0) {
    beginPendingClass();
    return;
  }
  showCoachLesson(coachQueue[0]);
}

btnClassStart.addEventListener("click", () => {
  const stageId = Number(stageSelect.value);
  if (stageId > core.getProgress().unlockedStage) {
    classDesc.textContent = "Stage locked — clear the previous Gatekeeper boss first.";
    sfx.play("miss");
    return;
  }
  const id = classSelect.value;
  const mins = Number(classMins.value);
  startClassWithCoach(id, mins);
});
btnClassStop.addEventListener("click", () => {
  pendingClassStart = null;
  hideCoach();
  core.stopClassSession();
  // Mid-stop: keep the abandoned class selected for resume.
  persistCampaignSelection();
  sfx.play("click");
});

document.querySelector("#btn-arena-abort")!.addEventListener("click", () => {
  const st = core.getStatus();
  if (st.exam.phase === "running") {
    core.stopExam();
    comboBanner.textContent = "EXAM ABORTED — no stage unlock";
  } else {
    core.stopClassSession();
    persistCampaignSelection();
    comboBanner.textContent = "CLASS ABORTED — same class stays selected to resume";
  }
  sfx.play("miss");
  comboBanner.classList.remove("is-hot");
  void enterMode("campaign", true);
});

btnCoachNext.addEventListener("click", () => {
  if (coachIndex >= coachQueue.length - 1) {
    beginPendingClass();
    return;
  }
  coachIndex += 1;
  showCoachLesson(coachQueue[coachIndex]);
});
btnCoachSkip.addEventListener("click", () => {
  beginPendingClass();
});

const examBrief = document.querySelector<HTMLElement>("#exam-brief")!;
const examBriefTitle = document.querySelector("#exam-brief-title")!;
const examBriefDesc = document.querySelector("#exam-brief-desc")!;
const examBriefRules = document.querySelector("#exam-brief-rules")!;
const examBriefPieces = document.querySelector("#exam-brief-pieces")!;
const examBriefMeta = document.querySelector("#exam-brief-meta")!;
const examTaskCard = document.querySelector<HTMLElement>("#exam-task-card")!;
const examTaskTitle = document.querySelector("#exam-task-title")!;
const examTaskBody = document.querySelector("#exam-task-body")!;
const examTaskNotes = document.querySelector("#exam-task-notes")!;
const missionNote = document.querySelector("#mission-note")!;
let pendingExamStage: number | null = null;

function hideExamBrief(): void {
  examBrief.hidden = true;
  pendingExamStage = null;
}

function showExamBrief(stageId: number): void {
  const briefing = getExamBriefing(stageId);
  if (!briefing) throw new Error(`No exam for stage ${stageId}`);
  hideCoach();
  pendingExamStage = stageId;
  const { exam, pieces } = briefing;
  examBriefTitle.textContent = exam.title;
  examBriefDesc.textContent = exam.description;
  examBriefRules.innerHTML = exam.rules.map((r) => `<li>${r}</li>`).join("");
  examBriefPieces.innerHTML = pieces
    .map((p, i) => `<li><strong>${i + 1}. ${p.title}</strong><br /><span>${p.task}</span></li>`)
    .join("");
  const mins = Math.round(exam.timeLimitSec / 60);
  examBriefMeta.textContent = `Pass ≥${exam.passAccuracyPct}% accuracy · Timer ${mins} min · Score = hits / (hits + wrongs) across all tasks · Piano keys stay blind (no green highlights) — staff + next note names tell you what to play.`;
  examBrief.hidden = false;
  focusScreen(examBrief, { select: true });
}

async function beginPendingExam(): Promise<void> {
  if (pendingExamStage == null) return;
  if (!ownsFullCourse()) {
    hideExamBrief();
    showFreeTrialPaywall("exam");
    return;
  }
  const stageId = pendingExamStage;
  const briefing = getExamBriefing(stageId);
  verifiedExamSessionId = null;
  const account = getAccountSnapshot();
  if (briefing && account.user && !account.isAnonymous) {
    try {
      const verified = await beginVerifiedExam(briefing.exam.id);
      verifiedExamSessionId = verified.sessionId;
    } catch (error) {
      comboBanner.textContent =
        error instanceof Error
          ? `Exam will run locally; server verification unavailable: ${error.message}`
          : "Exam will run locally; server verification unavailable.";
    }
  }
  hideExamBrief();
  core.startExamForStage(stageId);
  setGameMode("play");
  staffWrap.hidden = false;
  staff.resize();
  comboBanner.textContent = "EXAM LIVE — timed · ≥85% · no green key guides";
  comboBanner.classList.add("is-hot");
  sfx.play("levelUp");
  focusScreen(document.querySelector<HTMLElement>("#class-mission-bar"));
}

btnExamStart.addEventListener("click", () => {
  const stageId = Number(stageSelect.value);
  try {
    hideCoach();
    if (!ownsFullCourse()) {
      showFreeTrialPaywall("exam");
      return;
    }
    if (stageId > core.getProgress().unlockedStage) {
      throw new Error(
        `Stage ${stageId} Gatekeeper locked — clear Stage ${stageId - 1} exam first. Classes are optional practice.`,
      );
    }
    showExamBrief(stageId);
  } catch (err) {
    expectedHint.textContent =
      err instanceof Error
        ? err.message
        : "Cannot start exam — unlock this stage via earlier Gatekeepers.";
  }
});

document.querySelector("#btn-exam-begin")!.addEventListener("click", () => {
  const button = document.querySelector<HTMLButtonElement>("#btn-exam-begin");
  if (button) button.disabled = true;
  void beginPendingExam()
    .catch((err) => {
      expectedHint.textContent = err instanceof Error ? err.message : "Cannot start exam.";
    })
    .finally(() => {
      if (button) button.disabled = false;
    });
});
document.querySelector("#btn-exam-cancel")!.addEventListener("click", () => {
  hideExamBrief();
});

btnExamStop.addEventListener("click", () => {
  hideExamBrief();
  core.stopExam();
  expectedHint.textContent =
    "Start a class — dark green keys are what you must play. Wrong keys flash red.";
});

const stages = core.listStages();
stageSelect.innerHTML = stages
  .map((s) => `<option value="${s.id}">Stage ${s.id}: ${s.title}</option>`)
  .join("");

function persistCampaignSelection(): void {
  const stageId = Number(stageSelect.value);
  const classId = classSelect.value;
  const minutes = Number(classMins.value) || 0;
  if (!stageId || !classId || minutes <= 0) return;
  saveLastCampaign({ stageId, classId, minutes });
}

/** Restore Campaign focus. If saved class is already cleared → jump to next incomplete. */
function restoreCampaignSelection(): void {
  const last = readLastCampaign();
  const progress = core.getProgress();
  if (last) {
    const stage = stages.find((s) => s.id === last.stageId);
    const cls = stage?.classes.find((c) => c.id === last.classId);
    if (cls) {
      if (progress.completedClassIds.includes(last.classId)) {
        const nextId = pickNextCampaignClassId(last.classId);
        if (nextId) {
          const next = getClassById(nextId);
          focusCampaignClass(nextId, next?.defaultMinutes ?? last.minutes);
          return;
        }
      }
      stageSelect.value = String(last.stageId);
      refreshClassOptions();
      if ([...classSelect.options].some((o) => o.value === last.classId)) {
        classSelect.value = last.classId;
      }
      const mins = Math.max(
        cls.minMinutes,
        Math.min(cls.maxMinutes, last.minutes || cls.defaultMinutes),
      );
      classMins.value = String(mins);
      classMinsVal.textContent = classMins.value;
      refreshClassPlanUi();
      persistCampaignSelection();
      return;
    }
  }
  // No saved focus — open first incomplete on unlocked stage.
  const nextId = pickNextCampaignClassId(null);
  if (nextId) {
    const next = getClassById(nextId);
    focusCampaignClass(nextId, next?.defaultMinutes);
  }
}

function refreshLevelMap(): void {
  const progress = core.getProgress();
  const nodes: StageNode[] = stages.map((s) => {
    const done = s.classes.filter((c) => progress.completedClassIds.includes(c.id)).length;
    const examPassed = progress.passedExamIds.includes(s.examId);
    let state: StageNode["state"] = "locked";
    if (s.id < progress.unlockedStage || examPassed) state = "cleared";
    else if (s.id === progress.unlockedStage) state = "current";
    return {
      id: s.id,
      title: s.title,
      state,
      classesDone: done,
      classesTotal: s.classes.length,
    };
  });
  const selected = Number(stageSelect.value) || progress.unlockedStage;
  levelMap.setNodes(nodes, selected);
}

applyPremiumCampaignUnlock = () => {
  const p = core.getProgress();
  if (p.unlockedStage < MAX_CURRICULUM_STAGE) {
    core.setProgressState({ ...p, unlockedStage: MAX_CURRICULUM_STAGE });
    saveProgressEverywhere(core.getProgress());
    refreshLevelMap();
    comboBanner.textContent = "Full Course unlocked — all stages open.";
  }
};

levelMap.onSelect = (stageId) => {
  stageSelect.value = String(stageId);
  refreshClassOptions();
  persistCampaignSelection();
};

function updateClassStartButtonLabel(): void {
  const id = classSelect.value;
  const done = core.getProgress().completedClassIds.includes(id);
  btnClassStart.textContent = done ? "Restart class" : "Start class";
}

function pickNextCampaignClassId(finishedClassId?: string | null): string | null {
  const progress = core.getProgress();
  const completed = new Set(progress.completedClassIds);

  // Prefer the next class after the one just finished (same stage), then later stages.
  if (finishedClassId) {
    const finished = getClassById(finishedClassId);
    if (finished) {
      const stage = stages.find((s) => s.id === finished.stage);
      if (stage) {
        const idx = stage.classes.findIndex((c) => c.id === finishedClassId);
        const after = stage.classes.slice(idx + 1).find((c) => !completed.has(c.id));
        if (after) return after.id;
      }
      for (const s of stages) {
        if (s.id <= finished.stage) continue;
        if (s.id > progress.unlockedStage) break;
        const next = s.classes.find((c) => !completed.has(c.id));
        if (next) return next.id;
      }
    }
  }

  const recommendation = recommendRevision(progress, null, finishedClassId ?? undefined);
  if (recommendation?.classId && !completed.has(recommendation.classId)) {
    return recommendation.classId;
  }

  const stageId = finishedClassId
    ? (getClassById(finishedClassId)?.stage ?? progress.unlockedStage)
    : Number(stageSelect.value) || progress.unlockedStage;
  for (const s of stages) {
    if (s.id < stageId) continue;
    if (s.id > progress.unlockedStage) break;
    const next = s.classes.find((c) => !completed.has(c.id));
    if (next) return next.id;
  }
  return null;
}

/** Select + persist the Campaign dropdown focus (next after complete, or in-progress class). */
function focusCampaignClass(
  classId: string,
  minutes?: number,
  opts?: { enterCampaign?: boolean },
): void {
  const tutorClass = getClassById(classId);
  if (!tutorClass) return;
  if (opts?.enterCampaign) void enterMode("campaign", true);
  stageSelect.value = String(tutorClass.stage);
  refreshClassOptions();
  if ([...classSelect.options].some((o) => o.value === classId)) {
    classSelect.value = classId;
  }
  const mins = Math.max(
    tutorClass.minMinutes,
    Math.min(tutorClass.maxMinutes, minutes ?? tutorClass.defaultMinutes),
  );
  classMins.value = String(mins);
  classMinsVal.textContent = classMins.value;
  refreshClassPlanUi();
  updateClassStartButtonLabel();
  saveLastCampaign({ stageId: tutorClass.stage, classId, minutes: mins });
}

function handleClassReportClose(action: ResultsCloseAction): void {
  const ctx = lastCompletedClassContext;
  lastCompletedClassContext = null;
  if (action === "restart" && ctx) {
    // Restart keeps the completed class focused for a redo.
    focusCampaignClass(ctx.id, ctx.mins);
    startClassWithCoach(ctx.id, ctx.mins);
    return;
  }
  const nextId = pickNextCampaignClassId(ctx?.id ?? null);
  const trialDone =
    !ownsFullCourse() &&
    freeTrialCompletedCount(stages, core.getProgress().completedClassIds) >= FREE_TRIAL_LESSON_COUNT;
  if (trialDone && (!nextId || !canStartClass(nextId))) {
    void enterMode("campaign", true);
    refreshClassOptions();
    refreshLevelMap();
    comboBanner.classList.remove("is-hot");
    comboBanner.textContent = `All ${FREE_TRIAL_LESSON_COUNT} free lessons done — buy the Full Course to continue.`;
    showFreeTrialPaywall("trial-done");
    return;
  }
  if (nextId) {
    const nextClass = getClassById(nextId);
    focusCampaignClass(nextId, nextClass?.defaultMinutes ?? ctx?.mins, { enterCampaign: true });
  } else {
    void enterMode("campaign", true);
    if (ctx?.stageId) stageSelect.value = String(ctx.stageId);
    refreshClassOptions();
  }
  refreshLevelMap();
  comboBanner.classList.remove("is-hot");
  comboBanner.textContent = nextId
    ? "Next class selected — Start when ready."
    : "Campaign ready — pick any class to continue.";
  maybeShowEngagementPrompts({ examPassed: false });
}

restoreCampaignSelection();
if (!classSelect.value) {
  refreshClassOptions();
}

function showEngagementModal(opts: {
  kind: "buy" | "rate";
  title: string;
  body: string;
  primaryLabel: string;
  hideNever?: boolean;
  onPrimary: () => void;
  onLater: () => void;
  onNever: () => void;
}): void {
  let root = document.getElementById("engagement-modal");
  if (!root) {
    root = document.createElement("div");
    root.id = "engagement-modal";
    root.className = "engagement-modal";
    root.innerHTML = `
      <div class="engagement-card" role="dialog" aria-modal="true">
        <p class="engagement-kicker" id="eng-kicker"></p>
        <h3 id="eng-title"></h3>
        <p id="eng-body"></p>
        <div class="engagement-actions">
          <button type="button" class="btn-gold" id="eng-primary"></button>
          <button type="button" class="secondary" id="eng-later">Maybe later</button>
          <button type="button" class="secondary" id="eng-never">Don't ask again</button>
        </div>
      </div>`;
    document.body.appendChild(root);
  }
  root.querySelector("#eng-kicker")!.textContent =
    opts.kind === "buy" ? "UNLOCK MORE" : "RATE THE APP";
  root.querySelector("#eng-title")!.textContent = opts.title;
  root.querySelector("#eng-body")!.textContent = opts.body;
  const primary = root.querySelector<HTMLButtonElement>("#eng-primary")!;
  primary.textContent = opts.primaryLabel;
  root.querySelector<HTMLButtonElement>("#eng-never")!.hidden = !!opts.hideNever;
  root.hidden = false;
  const close = () => {
    root!.hidden = true;
  };
  primary.onclick = () => {
    close();
    opts.onPrimary();
  };
  root.querySelector<HTMLButtonElement>("#eng-later")!.onclick = () => {
    close();
    opts.onLater();
  };
  root.querySelector<HTMLButtonElement>("#eng-never")!.onclick = () => {
    close();
    opts.onNever();
  };
}

function maybeShowEngagementPrompts(opts: { examPassed: boolean }): void {
  let clears = 0;
  try {
    clears = Number(localStorage.getItem("upt_session_clears") || "0") || 0;
  } catch {
    clears = 0;
  }
  const snap = getAccountSnapshot();
  const hasFull = !snap.isAnonymous && snap.plan === "full_course";

  if (shouldShowBuyPrompt({ hasFullCourse: hasFull, clears })) {
    markBuyPromptShown();
    showEngagementModal({
      kind: "buy",
      title: "You're leveling up — unlock the full course?",
      body: `You've cleared several sessions. Full Course opens every lesson after the ${FREE_TRIAL_LESSON_COUNT} free ones, exams, certificates, and removes ads — one fair price.`,
      primaryLabel: "See Full Course",
      onPrimary: () => {
        void enterMode("home", true);
        document.querySelector<HTMLButtonElement>('[data-buy="full_course"]')?.click();
      },
      onLater: () => {},
      onNever: () => dismissBuyPromptForever(),
    });
    return;
  }

  if (shouldShowRatePrompt({ clears, examPassed: opts.examPassed })) {
    markRatePromptShown();
    showEngagementModal({
      kind: "rate",
      title: "Enjoying Ultimate Piano Tutor?",
      body: "A quick Play Store rating helps more students find classical piano training. It takes under a minute.",
      primaryLabel: "Rate on Play Store",
      onPrimary: () => {
        window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
        dismissRatePromptForever();
      },
      onLater: () => {},
      onNever: () => dismissRatePromptForever(),
    });
  }
}

function refreshClassOptions(): void {
  const stageId = Number(stageSelect.value);
  const stage = stages.find((s) => s.id === stageId);
  const progress = core.getProgress();
  if (!stage) return;
  const prevClassId = classSelect.value;
  const owns = ownsFullCourse();
  classSelect.innerHTML = stage.classes
    .map((c) => {
      const cleared = progress.completedClassIds.includes(c.id) ? " ✓" : "";
      const lock = owns || isClassInFreeTrial(stages, c.id) ? "" : "🔒 ";
      return `<option value="${c.id}">${lock}${c.code} ${c.title}${cleared} (${c.defaultMinutes} min)</option>`;
    })
    .join("");
  if (prevClassId && [...classSelect.options].some((o) => o.value === prevClassId)) {
    classSelect.value = prevClassId;
  }
  refreshClassPlanUi();
  updateClassStartButtonLabel();
  persistCampaignSelection();
}

function refreshClassPlanUi(): void {
  const cls = core.getCurriculumClass(classSelect.value);
  if (!cls) return;
  classMins.min = String(cls.minMinutes);
  classMins.max = String(cls.maxMinutes);
  if (Number(classMins.value) < cls.minMinutes || Number(classMins.value) > cls.maxMinutes) {
    classMins.value = String(cls.defaultMinutes);
  }
  classMinsVal.textContent = classMins.value;
  const skills = cls.skills?.length ? ` · Skills: ${cls.skills.join(", ")}` : "";
  const assessment = cls.assessment ? ` · Mode: ${cls.assessment}` : "";
  classDesc.textContent = `${cls.description}${skills}${assessment} · Default ${cls.defaultMinutes} min (${cls.minMinutes}–${cls.maxMinutes}).`;
  const plan = core.previewClassPlan(cls.id, Number(classMins.value));
  const loops = plan.segments.map((s) => `${s.label ?? s.pieceId}×${s.loops}`).join(" · ");
  classPlanMeta.textContent = `${classMins.value} min kind-faithful session · seed: ${loops} · longer time = more style-matched practice.`;
  updateClassStartButtonLabel();
}

stageSelect.addEventListener("change", () => {
  refreshClassOptions();
  persistCampaignSelection();
});
classSelect.addEventListener("change", () => {
  refreshClassPlanUi();
  persistCampaignSelection();
});
classMins.addEventListener("input", () => {
  classMinsVal.textContent = classMins.value;
  refreshClassPlanUi();
  persistCampaignSelection();
});
// Keep restored selection — do not rebuild options again here.
persistCampaignSelection();

function pulseCountFor(sig: TimeSignatureId): { count: number; accents: number[] } {
  switch (sig) {
    case "3/4":
      return { count: 3, accents: [0] };
    case "2/4":
      return { count: 2, accents: [0] };
    case "6/8":
      return { count: 6, accents: [0, 3] };
    default:
      return { count: 4, accents: [0] };
  }
}

function rebuildPulseLights(sig: TimeSignatureId): void {
  const { count, accents } = pulseCountFor(sig);
  pulseRow.innerHTML = Array.from({ length: count }, (_, i) => {
    const accent = accents.includes(i) ? " accent" : "";
    return `<div class="pulse${accent}" data-pulse="${i}"></div>`;
  }).join("");
}

rebuildPulseLights("4/4");

statusGrid.innerHTML = `
  <div class="status-item"><div class="label">Audio</div><div class="value" id="st-audio">—</div></div>
  <div class="status-item"><div class="label">Play source</div><div class="value" id="st-source">—</div></div>
  <div class="status-item"><div class="label">MIDI</div><div class="value" id="st-midi">—</div></div>
  <div class="status-item"><div class="label">Active notes</div><div class="value" id="st-notes">—</div></div>
`;

classSessionStats.innerHTML = `
  <div class="status-item"><div class="label">Session</div><div class="value" id="st-class-phase">—</div></div>
  <div class="status-item"><div class="label">Segment</div><div class="value" id="st-class-seg">—</div></div>
  <div class="status-item"><div class="label">Loop</div><div class="value" id="st-class-loop">—</div></div>
  <div class="status-item"><div class="label">Progress</div><div class="value" id="st-class-prog">—</div></div>
`;

metroStats.innerHTML = `
  <div class="status-item"><div class="label">Metronome</div><div class="value" id="st-metro">—</div></div>
  <div class="status-item"><div class="label">Sync accuracy</div><div class="value" id="st-acc">—</div></div>
  <div class="status-item"><div class="label">Perfect / Good</div><div class="value" id="st-pg">—</div></div>
  <div class="status-item"><div class="label">Timing bias</div><div class="value" id="st-bias">—</div></div>
`;

const stAudio = () => document.querySelector("#st-audio")!;
const stSource = () => document.querySelector("#st-source")!;
const stMidi = () => document.querySelector("#st-midi")!;
const stNotes = () => document.querySelector("#st-notes")!;
const stMetro = () => document.querySelector("#st-metro")!;
const stAcc = () => document.querySelector("#st-acc")!;
const stPg = () => document.querySelector("#st-pg")!;
const stBias = () => document.querySelector("#st-bias")!;
const stClassPhase = () => document.querySelector("#st-class-phase")!;
const stClassSeg = () => document.querySelector("#st-class-seg")!;
const stClassLoop = () => document.querySelector("#st-class-loop")!;
const stClassProg = () => document.querySelector("#st-class-prog")!;

let lastDeviceKey = "";
let lastKbSig = "";
let activeNoteSnap = "";

function flashPulse(e: MetronomeBeatEvent): void {
  const nodes = pulseRow.children;
  for (let i = 0; i < nodes.length; i++) {
    (nodes[i] as HTMLElement).classList.toggle("on", i === e.pulseIndex);
  }
}

function setValue(el: Element, text: string, ok?: boolean): void {
  const node = el as HTMLElement;
  if (node.textContent !== text) node.textContent = text;
  node.classList.toggle("ok", !!ok);
  node.classList.toggle("warn", ok === false);
}

function formatClassClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

const missionBar = document.querySelector<HTMLElement>("#class-mission-bar")!;
const missionTitle = document.querySelector("#mission-title")!;
const missionTimer = document.querySelector("#mission-timer")!;
const missionProg = document.querySelector("#mission-prog")!;
const missionHits = document.querySelector("#mission-hits")!;
const missionMiss = document.querySelector("#mission-miss")!;
const missionAcc = document.querySelector("#mission-acc")!;
const missionSeg = document.querySelector("#mission-seg")!;
const missionFill = document.querySelector<HTMLElement>("#mission-fill")!;
const btnPerformanceDownload =
  document.querySelector<HTMLButtonElement>("#btn-performance-download")!;
let classClockIv: ReturnType<typeof setInterval> | null = null;

btnPerformanceDownload.addEventListener("click", () => {
  const recording = core.getLastRecordedPerformance();
  if (!recording?.events.length) return;
  const blob = new Blob([JSON.stringify(recording, null, 2)], {
    type: "application/json",
  });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${recording.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-performance.json`;
  link.click();
  URL.revokeObjectURL(href);
});

function stopMissionClock(): void {
  if (classClockIv != null) {
    clearInterval(classClockIv);
    classClockIv = null;
  }
}

function updateClassMissionBar(s: CoreEngineStatus): void {
  const examLive = s.exam.phase === "running";
  const cs = s.classSession;
  const classLive = !examLive && cs.phase === "running";
  const inPlay = gameShell.dataset.mode === "play";
  const show = inPlay && (examLive || classLive || cs.phase === "completed");
  missionBar.hidden = !show;
  btnPerformanceDownload.hidden =
    cs.phase !== "completed" || !(core.getLastRecordedPerformance()?.events.length);
  missionBar.classList.toggle("is-exam", examLive);
  examTaskCard.hidden = !examLive;

  if (!show) {
    stopMissionClock();
    return;
  }

  if (examLive) {
    const ex = s.exam;
    const donePieces = ex.pieceIndex;
    const pieceFrac =
      ex.stats.expectedCount > 0
        ? 1 - ex.stats.remaining / Math.max(ex.stats.expectedCount, 1)
        : 0;
    const prog01 = ex.pieceCount
      ? Math.min(1, (donePieces + pieceFrac) / ex.pieceCount)
      : 0;
    missionTitle.textContent = `EXAM · ${ex.title ?? "Gatekeeper"}`;
    missionTimer.textContent = formatClassClock(ex.remainingSec ?? 0);
    missionProg.textContent = `${Math.round(prog01 * 100)}%`;
    missionHits.textContent = String(ex.stats.hits);
    missionMiss.textContent = String(ex.stats.wrongs);
    missionAcc.textContent = `${ex.stats.accuracyPct.toFixed(0)}%`;
    missionSeg.textContent = `Task ${ex.pieceIndex + 1}/${ex.pieceCount} · ${ex.currentPieceTitle ?? "—"}`;
    missionFill.style.width = `${Math.max(2, Math.round(prog01 * 100))}%`;
    missionNote.innerHTML = `Countdown left (need ≥${ex.passAccuracyPct}%). Piano stays blind — play the <em>Next</em> notes / staff. Abort cancels with no unlock.`;
    examTaskTitle.textContent = ex.currentPieceTitle ?? `Task ${ex.pieceIndex + 1}`;
    examTaskBody.textContent = ex.currentTask ?? ex.description ?? "Play the next expected notes.";
    examTaskNotes.textContent = ex.nextNoteLabels?.length
      ? ex.nextNoteLabels.join(" + ")
      : "—";

    if (classClockIv == null) {
      classClockIv = setInterval(() => {
        (core as { tickExam?: () => void }).tickExam?.();
        const st = core.getStatus();
        if (st.exam.phase !== "running") {
          stopMissionClock();
          updateClassMissionBar(st);
          return;
        }
        updateClassMissionBar(st);
      }, 250);
    }
    return;
  }

  missionTitle.textContent = cs.classTitle ? `CLASS · ${cs.classTitle}` : "CLASS LIVE";
  const remain = cs.remainingSec ?? 0;
  missionTimer.textContent = `${formatClassClock(remain)} left`;
  missionProg.textContent = `${Math.round(cs.progress01 * 100)}%`;
  missionHits.textContent = String(cs.practiceStats?.hits ?? 0);
  missionMiss.textContent = String(cs.practiceStats?.wrongs ?? 0);
  missionAcc.textContent =
    cs.practiceStats != null ? `${cs.practiceStats.accuracyPct.toFixed(0)}%` : "—";
  missionSeg.textContent = cs.segmentLabel
    ? `${cs.segmentIndex + 1}/${cs.segmentCount} · ${cs.segmentLabel} · loop ${Math.min(cs.loopIndex + 1, cs.loopCount)}/${cs.loopCount}`
    : "—";
  missionFill.style.width = `${Math.max(2, Math.round(cs.progress01 * 100))}%`;
  const totalMin = cs.selectedMinutes || cs.estimatedMinutes || "—";
  missionNote.innerHTML = cs.fillingTime
    ? `Challenge phase — new combos to lock muscle memory · <strong>${totalMin} min</strong> session.`
    : `Full <strong>${totalMin} min</strong> · varied drills (warm → build → challenge → lock) · <strong>${formatClassClock(remain)}</strong> left.`;

  if (classLive && classClockIv == null) {
    classClockIv = setInterval(() => {
      const st = core.getStatus();
      if (st.classSession.phase !== "running") {
        stopMissionClock();
        return;
      }
      const c = st.classSession;
      missionTimer.textContent = `${formatClassClock(c.remainingSec ?? 0)} left`;
      missionProg.textContent = `${Math.round(c.progress01 * 100)}%`;
      missionFill.style.width = `${Math.max(2, Math.round(c.progress01 * 100))}%`;
      if (c.practiceStats) {
        missionHits.textContent = String(c.practiceStats.hits);
        missionMiss.textContent = String(c.practiceStats.wrongs);
        missionAcc.textContent = `${c.practiceStats.accuracyPct.toFixed(0)}%`;
      }
    }, 250);
  }
}

async function submitCurrentExamVerification(): Promise<void> {
  if (verifiedExamSubmitBusy || !verifiedExamSessionId) return;
  const attempt = core.getExamAttemptData();
  if (!attempt) return;
  const sessionId = verifiedExamSessionId;
  verifiedExamSessionId = null;
  verifiedExamSubmitBusy = true;
  try {
    const result = await submitVerifiedExam(sessionId, attempt);
    comboBanner.textContent = result.passed
      ? `SERVER VERIFIED · ${result.examId} · ${result.accuracyPct.toFixed(1)}%`
      : `Exam result was not server-verified (${result.accuracyPct.toFixed(1)}%).`;
    if (result.passed) {
      comboBanner.classList.add("is-hot");
      refreshCertificateUi();
    }
  } catch (error) {
    comboBanner.textContent =
      error instanceof Error
        ? `Local result saved, but server verification failed: ${error.message}`
        : "Local result saved, but server verification failed.";
  } finally {
    verifiedExamSubmitBusy = false;
  }
}

function renderStatus(s: CoreEngineStatus): void {
  sfx.setMuted(s.audioMuted);
  if (s.audioReady && core.synth.audioContext) sfx.attach(core.synth.audioContext);

  const audioText = s.audioMuted
    ? "Muted"
    : s.audioReady
      ? "On (auto)"
      : "Waiting for first key/click";
  setValue(stAudio(), audioText, s.audioReady && !s.audioMuted);

  const sourceText =
    s.playInput === "midi"
      ? s.midiListening && s.midiDevices.length
        ? "MIDI keyboard"
        : "MIDI selected (no device)"
      : "Laptop keyboard";
  setValue(stSource(), sourceText, true);

  const midiLabel = !s.midi.supported
    ? "Unsupported in this browser"
    : !s.midi.accessGranted
      ? (s.midi.error ?? "Permission needed")
      : s.midiDevices.length === 0
        ? "None detected → laptop keys"
        : s.midiListening
          ? `${s.midiDevices.length} connected`
          : "Disconnected by you";
  setValue(
    stMidi(),
    midiLabel,
    s.midiDevices.length > 0 && s.midiListening ? true : s.midiDevices.length === 0 ? undefined : false,
  );

  const notesText = s.activeNotes.length ? s.activeNotes.join(", ") : "—";
  setValue(stNotes(), notesText);
  if (notesText !== activeNoteSnap) {
    activeNoteSnap = notesText;
    piano.setActiveNotes(s.activeNotes);
    const wrong = (s.keyFeedback ?? [])
      .filter((f) => f.kind === "wrong")
      .map((f) => f.midi);
    const hit = (s.keyFeedback ?? []).filter((f) => f.kind === "hit").map((f) => f.midi);
    piano.setKeyFeedback(wrong, hit);
    if (s.notationMode === "staff") staff.setNotes(s.activeNotes);
  }

  if (notationModeEl.value !== s.notationMode) {
    notationModeEl.value = s.notationMode;
    piano.setNotationMode(s.notationMode);
    staffWrap.hidden = s.notationMode !== "staff";
    if (s.notationMode === "letter" && showingComputerKeyLabels) syncComputerKeyLabelOverlay();
    else if (s.notationMode !== "letter") piano.setComputerKeyLabels(null);
  }

  if (playInput.value !== s.playInput) playInput.value = s.playInput;
  const useComputerLabels = s.qwertyEnabled || s.playInput === "laptop";
  if (showingComputerKeyLabels !== useComputerLabels) {
    const wasComputerLabels = showingComputerKeyLabels === true;
    showingComputerKeyLabels = useComputerLabels;
    if (useComputerLabels && s.notationMode === "letter") {
      syncComputerKeyLabelOverlay();
      kbSizeHint.textContent =
        "Laptop mode: piano keys show A–P / W–P; hold Shift to remount labels one octave up.";
    } else {
      piano.setComputerKeyLabels(null);
      if (wasComputerLabels && useComputerLabels === false) {
        // Leaving laptop overlay — keep user's chosen notation (don't force letter).
      }
    }
  }

  const st = s.metronome.syncStats;
  const rush =
    st.attempts === 0
      ? "—"
      : st.meanDeltaMs < -8
        ? `Rushing ${Math.abs(st.meanDeltaMs).toFixed(0)}ms`
        : st.meanDeltaMs > 8
          ? `Dragging ${st.meanDeltaMs.toFixed(0)}ms`
          : "Centered";

  setValue(
    stMetro(),
    s.metronome.running ? `${s.metronome.bpm} BPM · ${s.metronome.timeSignature}` : "Stopped",
    s.metronome.running,
  );
  setValue(stAcc(), s.metronome.lockGrading ? `${st.accuracyPct.toFixed(0)}%` : "Off");
  setValue(stPg(), `${st.perfect} / ${st.good}`);
  setValue(stBias(), rush);

  const cs = s.classSession;
  setValue(
    stClassPhase(),
    cs.phase === "idle" ? "Idle" : cs.phase === "running" ? "Running" : cs.phase,
    cs.phase === "running" || cs.phase === "completed",
  );
  setValue(
    stClassSeg(),
    cs.classTitle
      ? `${cs.segmentIndex + 1}/${cs.segmentCount}${cs.segmentLabel ? ` · ${cs.segmentLabel}` : ""}`
      : "—",
  );
  setValue(
    stClassLoop(),
    cs.phase === "idle" ? "—" : `${Math.min(cs.loopIndex + 1, Math.max(cs.loopCount, 1))}/${cs.loopCount || "—"}`,
  );
  setValue(stClassProg(), `${Math.round(cs.progress01 * 100)}%`);

  updateClassMissionBar(s);

  const ip = s.importedPractice;
  const practiceModeLabel = document.querySelector("#practice-mode-label");
  if (practiceModeLabel) {
    if (cs.phase === "running") practiceModeLabel.textContent = "Class session";
    else if (s.exam.phase === "running") practiceModeLabel.textContent = "Gatekeeper exam";
    else if (ip.active) {
      practiceModeLabel.textContent =
        ip.mode === "learn" ? "MIDI Learn" : "MIDI Performance";
    } else practiceModeLabel.textContent = "Free warm-up";
  }

  const expected = ip.active
    ? ip.expectedMidis
    : (cs.expectedMidis ?? []);
  const expectedFingers = ip.active ? (ip.expectedFingers ?? {}) : (cs.expectedFingers ?? {});
  if ((cs.phase === "running" || (ip.active && ip.mode === "learn")) && expected.length) {
    piano.setLessonFingers(new Map(Object.entries(expectedFingers).map(([k, v]) => [Number(k), v])));
  } else if (!ip.active) {
    piano.setLessonFingers(null);
  }
  if (s.blindMode || s.exam.phase === "running") {
    piano.setGuideNotes([]);
    const ex = s.exam;
    const next =
      ex.nextNoteLabels?.length > 0
        ? ex.nextNoteLabels.join(" + ")
        : "…";
    staff.setNotes(ex.expectedMidis?.length ? ex.expectedMidis : []);
    staffWrap.hidden = false;
    staff.resize();
    expectedHint.textContent = `EXAM · Task ${ex.pieceIndex + 1}/${ex.pieceCount}: ${ex.currentPieceTitle ?? "—"} · Play ${next} · Acc ${ex.stats.accuracyPct.toFixed(0)}% (need ≥${ex.passAccuracyPct}%) · ${formatClassClock(ex.remainingSec)} left`;
  } else if (ip.active && expected.length) {
    piano.setGuideNotes(expected);
    const st = ip.stats;
    const acc = st
      ? ` · hits ${st.hits} / miss ${st.wrongs} · ${st.accuracyPct.toFixed(0)}%`
      : "";
    expectedHint.textContent =
      ip.mode === "learn"
        ? `Learn · play highlighted key(s)${acc}`
        : `Performance · stay with the beat${acc}`;
    if (ip.active) {
      missionBar.hidden = false;
      missionTitle.textContent =
        ip.mode === "learn" ? `LEARN · ${ip.title ?? "MIDI"}` : `PERFORM · ${ip.title ?? "MIDI"}`;
      if (st) {
        const prog =
          st.expectedCount > 0
            ? Math.round(((st.expectedCount - st.remaining) / st.expectedCount) * 100)
            : 0;
        missionProg.textContent = `${prog}%`;
        missionFill.style.width = `${prog}%`;
        missionHits.textContent = String(st.hits);
        missionMiss.textContent = String(st.wrongs);
        missionAcc.textContent = `${st.accuracyPct.toFixed(0)}%`;
        missionSeg.textContent = ip.mode === "learn" ? "Wait for key" : "Keep time";
        missionTimer.textContent = formatClassClock(st.elapsedSec);
      }
    }
  } else if (cs.phase === "running" && expected.length) {
    const earMode = cs.assessment === "ear";
    const blindMode = cs.assessment === "blind";
    const sightMode = cs.assessment === "sight";
    const hideGuides = earMode || blindMode;
    piano.setGuideNotes(hideGuides ? [] : expected);
    if (sightMode) {
      staff.setNotes(expected);
      staffWrap.hidden = false;
      staff.resize();
    } else if (earMode || blindMode) {
      staffWrap.hidden = blindMode ? false : true;
      if (blindMode) {
        staff.setNotes(expected);
        staff.resize();
      }
    }
    const acc = cs.practiceStats
      ? ` · hits ${cs.practiceStats.hits} / wrong ${cs.practiceStats.wrongs} · ${cs.practiceStats.accuracyPct.toFixed(0)}%`
      : "";
    const fingerHint = Object.entries(expectedFingers)
      .map(([midi, finger]) => `${labelForMidi(Number(midi), "letter")}→${finger}`)
      .join(" · ");
    const coachHint = cs.gradingHint
      ? cs.gradingHint
      : fingerHint
        ? `Use finger ${fingerHint}`
        : null;
    expectedHint.textContent = earMode
      ? `Ear mode · reproduce the phrase from memory (no key/staff guide)${acc}`
      : blindMode
        ? `Blind mode · no green keys — use staff / memory only${acc}`
      : sightMode
        ? `Sight mode · read the staff and keep moving${acc}`
        : coachHint
          ? `${coachHint}${acc}`
          : `Play the highlighted key(s) only — lift extra fingers${acc}`;
  } else if (cs.phase === "completed") {
    const recommendation = recommendRevision(
      s.progress,
      cs.practiceStats as FollowAlongStats | null,
      cs.classId,
    );
    expectedHint.textContent = recommendation
      ? `Class complete · Recommended: ${recommendation.title} — ${recommendation.reason}`
      : "Class complete. You can take this stage’s Gatekeeper — need ≥85% accuracy.";
  } else if (s.exam.phase === "passed") {
    expectedHint.textContent = `Exam PASSED (≥${s.exam.passAccuracyPct}%) — Stage ${s.progress.unlockedStage} unlocked.`;
  } else if (s.exam.phase === "failed") {
    const why =
      s.exam.failReason === "timeout"
        ? "Time ran out."
        : `Accuracy ${s.exam.stats.accuracyPct.toFixed(0)}% (need ≥${s.exam.passAccuracyPct}%).`;
    expectedHint.textContent = `Exam failed — ${why} Practice the stage classes, then retry.`;
  } else if (!ip.active) {
    expectedHint.textContent = "Green keys = next note. Start a class from Campaign, or import MIDI in Vault.";
  }

  if (cs.phase === "completed" && lastClassPhase !== "completed" && !results.visible && !arcadeHost.active) {
    bumpSessionClears();
    const tutorClass = cs.classId ? getClassById(cs.classId) : undefined;
    lastCompletedClassContext = cs.classId
      ? {
          id: cs.classId,
          mins: cs.selectedMinutes,
          stageId: tutorClass?.stage ?? s.progress.unlockedStage,
        }
      : null;
    // Immediately focus NEXT class so reopen / continue never lands on the finished one.
    if (cs.classId) {
      const nextId = pickNextCampaignClassId(cs.classId);
      if (nextId) {
        const nextClass = getClassById(nextId);
        focusCampaignClass(nextId, nextClass?.defaultMinutes ?? cs.selectedMinutes);
      }
    }
    const ps = (cs.practiceStats ?? {
      hits: 0,
      wrongs: 0,
      accuracyPct: 0,
      remaining: 0,
      completed: true,
      perfect: 0,
      early: 0,
      late: 0,
      meanDeltaMs: 0,
      jitterMs: 0,
      velocityHits: 0,
      velocityMisses: 0,
      notesPerMinute: 0,
      elapsedSec: cs.elapsedSec ?? 0,
      errorByMidi: {},
      expectedCount: 0,
    }) as FollowAlongStats;
    const report = buildReportFromStats(
      "CLASS CLEAR",
      cs.classTitle ?? "Mission complete",
      ps,
      `Full ${cs.selectedMinutes} min completed · Gatekeeper still optional on this stage.`,
    );
    const sessionScore = computeClassSessionScore(ps);
    const studentName =
      getAccountSnapshot()?.displayName ||
      profileName.value ||
      profile.name ||
      "Piano Student";
    results.show(
      {
        kind: "report",
        title: "CLASS CLEAR",
        subtitle: cs.classTitle ?? "Mission complete",
        score: sessionScore,
        showRestart: true,
        share: {
          studentName,
          classTitle: cs.classTitle ?? "Class clear",
          score: sessionScore,
          grade: report.letter,
          accuracyPct: ps.accuracyPct,
          npm: report.npm,
          hits: ps.hits,
          wrongs: ps.wrongs,
          minutes: cs.selectedMinutes,
          stageLabel: tutorClass ? `STAGE ${tutorClass.stage} · ${tutorClass.code}` : "CLASS CLEAR",
          perfect: ps.perfect,
        },
        lines: [
          { label: "Grade", value: report.letter },
          {
            label: "Score",
            value: String(sessionScore),
            tip: "Accuracy×50 + Perfect×8 + Hits×2 − Wrongs×6 + steady-timing bonus (up to 400).",
          },
          {
            label: "NPM",
            value: String(report.npm),
            tip: "Notes cleared per minute — typing-tutor pace.",
          },
          { label: "Accuracy", value: `${ps.accuracyPct.toFixed(0)}%` },
          {
            label: "Jitter",
            value: `${ps.jitterMs.toFixed(0)} ms`,
            tip: "Timing spread — lower means steadier rhythm.",
          },
          {
            label: "Bias",
            value: `${ps.meanDeltaMs >= 0 ? "+" : ""}${ps.meanDeltaMs.toFixed(0)} ms`,
            tip: "Average early (−) or late (+) vs the beat.",
          },
          {
            label: "Practice",
            value: `${cs.selectedMinutes} min`,
            tip: "Wall-clock practice time you committed to.",
          },
          { label: "XP banked", value: "+120" },
        ],
        report,
      },
      handleClassReportClose,
    );
  }
  if (
    (s.exam.phase === "passed" || s.exam.phase === "failed") &&
    s.exam.phase !== lastExamPhase
  ) {
    void submitCurrentExamVerification();
  }
  if (s.exam.phase === "passed" && lastExamPhase !== "passed" && !results.visible && !arcadeHost.active) {
    bumpSessionClears();
    const report = buildReportFromStats(
      "GATEKEEPER DOWN",
      s.exam.title ?? "Stage exam cleared",
      s.exam.stats as FollowAlongStats,
      `Stage ${s.progress.unlockedStage} unlocked · print PDF for your records.`,
    );
    results.show(
      {
        kind: "report",
        title: "GATEKEEPER DOWN",
        subtitle: s.exam.title ?? "Stage exam cleared",
        score: Math.round(s.exam.stats.accuracyPct * 40),
        lines: [
          { label: "Grade", value: report.letter },
          { label: "NPM", value: String(report.npm) },
          { label: "Accuracy", value: `${s.exam.stats.accuracyPct.toFixed(0)}%` },
          { label: "Perfect", value: String(s.exam.stats.perfect ?? 0) },
          { label: "Jitter", value: `${(s.exam.stats.jitterMs ?? 0).toFixed(0)} ms` },
          { label: "Stage unlocked", value: String(s.progress.unlockedStage) },
          { label: "XP banked", value: "+500" },
        ],
        report,
      },
      () => maybeShowEngagementPrompts({ examPassed: true }),
    );
  }
  if (s.exam.phase === "failed" && lastExamPhase !== "failed" && !results.visible && !arcadeHost.active) {
    const report = buildReportFromStats(
      s.exam.failReason === "timeout" ? "TIME UP" : "BOSS RETAINED",
      s.exam.title ?? "Gatekeeper",
      s.exam.stats as FollowAlongStats,
    );
    results.show({
      kind: "lose",
      title: s.exam.failReason === "timeout" ? "TIME UP" : "BOSS RETAINED",
      subtitle:
        s.exam.failReason === "timeout"
          ? "Timer expired before all tasks finished."
          : "Practice the stage classes, then rematch.",
      score: Math.round(s.exam.stats.accuracyPct * 25),
      lines: [
        { label: "Grade", value: report.letter },
        { label: "Accuracy", value: `${s.exam.stats.accuracyPct.toFixed(0)}%` },
        { label: "Need", value: `${s.exam.passAccuracyPct}%` },
        { label: "Jitter", value: `${(s.exam.stats.jitterMs ?? 0).toFixed(0)} ms` },
        { label: "Time left", value: formatClassClock(s.exam.remainingSec ?? 0) },
      ],
      report,
    });
  }
  lastClassPhase = cs.phase;
  lastExamPhase = s.exam.phase;

  if (ip.completed && !lastImportedCompleted && ip.stats && !results.visible && !arcadeHost.active) {
    const title = ip.mode === "learn" ? "MIDI LEARN COMPLETE" : "MIDI PERFORMANCE";
    const studentName =
      getAccountSnapshot()?.displayName ||
      profileName.value ||
      "Piano Student";
    const sessionScore = Math.round(ip.stats.accuracyPct * 20 + ip.stats.hits * 12);
    const report = buildReportFromStats(
      title,
      ip.title ?? "Imported MIDI",
      ip.stats,
      ip.mode === "learn"
        ? "Learn clear — try Performance mode for timing."
        : "Performance graded — check misses and timing jitter.",
    );
    const mins = 0;
    results.show(
      {
        kind: "report",
        title,
        subtitle: ip.title ?? "Imported MIDI",
        score: sessionScore,
        showRestart: true,
        share: {
          studentName,
          classTitle: ip.title ?? "Imported MIDI",
          score: sessionScore,
          grade: report.letter,
          accuracyPct: ip.stats.accuracyPct,
          npm: report.npm,
          hits: ip.stats.hits,
          wrongs: ip.stats.wrongs,
          minutes: mins,
          stageLabel: ip.mode === "learn" ? "LEARN CLEAR" : "PERFORMANCE",
          perfect: ip.stats.perfect,
        },
        lines: [
          { label: "Grade", value: report.letter },
          { label: "Accuracy", value: `${ip.stats.accuracyPct.toFixed(0)}%` },
          { label: "Hits", value: String(ip.stats.hits) },
          { label: "Misses", value: String(ip.stats.wrongs) },
          { label: "Perfect", value: String(ip.stats.perfect) },
          { label: "Jitter", value: `${ip.stats.jitterMs.toFixed(0)} ms` },
        ],
        report,
      },
      ((savedMode) => (action: string) => {
        if (action === "restart" && savedMode) void startImportedMode(savedMode);
      })(ip.mode),
    );
    setImportButtons(!!core.getImportedPiece(), false);
    core.stopImportedPractice();
    refreshCertificateUi();
    void recordPractice().then(() => refreshStreakBar());
  }
  lastImportedCompleted = !!ip.completed;

  if (ip.active && ip.mode === "performance" && !ip.completed) {
    if (!importedPerfRaf) {
      const loop = () => {
        importedPerfRaf = 0;
        const st = core.getStatus().importedPractice;
        if (st.active && st.mode === "performance" && !st.completed) {
          core.tickImportedPractice();
          importedPerfRaf = requestAnimationFrame(loop);
        }
      };
      importedPerfRaf = requestAnimationFrame(loop);
    }
  } else if (importedPerfRaf) {
    cancelAnimationFrame(importedPerfRaf);
    importedPerfRaf = 0;
  }

  const progressEl = document.querySelector("#progress-meta");
  if (progressEl) {
    progressEl.textContent = `Stage ${s.progress.unlockedStage}/${MAX_CURRICULUM_STAGE} · Classes ${s.progress.completedClassIds.length} · Exams ${s.progress.passedExamIds.length}/${STAGE_EXAMS.length}`;
  }

  hudStage.innerHTML = `Stage <strong>${s.progress.unlockedStage}/${MAX_CURRICULUM_STAGE}</strong>`;
  const cleared = s.progress.passedExamIds.length;
  const rank =
    cleared >= 9 ? "VIRTUOSO" : cleared >= 7 ? "MAESTRO" : cleared >= 5 ? "ACE" : cleared >= 3 ? "RISING" : cleared >= 1 ? "CADET" : "TRAINEE";
  hudRank.innerHTML = `Rank <strong>${rank}</strong>`;
  topRank.innerHTML = `Rank <strong>${rank}</strong>`;
  const xp =
    s.progress.completedClassIds.length * 120 + s.progress.passedExamIds.length * 500;
  hudXp.innerHTML = `XP <strong>${xp}</strong>`;
  hudClears.innerHTML = `Clears <strong>${s.progress.completedClassIds.length}</strong>`;
  const xpPct = Math.min(100, (xp % 2000) / 20);
  xpFill.style.width = `${Math.max(8, xpPct)}%`;
  if (lastXpShown >= 0 && xp > lastXpShown) sfx.play("levelUp");
  lastXpShown = xp;

  if (gameShell.dataset.mode === "campaign") refreshLevelMap();

  // Live hands guide during class / MIDI learn (hidden in exam blind mode)
  if ((cs.phase === "running" || (ip.active && ip.mode === "learn")) && !s.blindMode && expected.length) {
    handsGuide.setExpected(expected, expectedFingers);
  } else if (s.blindMode) {
    handsGuide.hide();
  } else if (cs.phase !== "running" && !(ip.active && ip.mode === "learn")) {
    handsGuide.hide();
  }

  const feedback = s.keyFeedback ?? [];
  const fbSig = feedback.map((f) => `${f.midi}:${f.kind}:${Math.floor(f.until)}`).join("|");
  if (fbSig && fbSig !== lastFeedbackSig) {
    lastFeedbackSig = fbSig;
    if (feedback.some((f) => f.kind === "hit")) {
      hitStreak += 1;
      comboBanner.textContent =
        hitStreak >= 8 ? `COMBO ×${hitStreak} — ON FIRE` : `STREAK ×${hitStreak}`;
      comboBanner.classList.toggle("is-hot", hitStreak >= 3);
      const midi = feedback.find((f) => f.kind === "hit")!.midi;
      const pos = piano.keyNormPos(midi);
      const kind = hitStreak >= 5 ? "perfect" : "hit";
      hitFx.burst(kind, pos.nx, pos.ny);
      // Correct key: piano tone only — no UI SFX on top.
    } else if (feedback.some((f) => f.kind === "wrong")) {
      hitStreak = 0;
      comboBanner.textContent = "MISS — reset streak";
      comboBanner.classList.remove("is-hot");
      const midi = feedback.find((f) => f.kind === "wrong")!.midi;
      const pos = piano.keyNormPos(midi);
      hitFx.burst("wrong", pos.nx, pos.ny);
      sfx.play("miss");
    }
  } else if (!feedback.length) {
    lastFeedbackSig = "";
    if (cs.phase === "running") {
      if (!comboBanner.textContent.startsWith("STREAK") && !comboBanner.textContent.startsWith("COMBO")) {
        comboBanner.textContent = "CLASS LIVE — nail the next note";
      }
    } else if (s.blindMode) {
      comboBanner.textContent = "GATEKEEPER — BLIND MODE";
      comboBanner.classList.add("is-hot");
    } else if (!s.audioReady) {
      comboBanner.textContent = "Ready — press a key to unlock audio";
      comboBanner.classList.remove("is-hot");
    }
  }

  // Persist progress only when curriculum progress actually changes.
  // Writing localStorage on every note status frame causes Android tap jank.
  const progSig =
    `${s.progress.unlockedStage}|` +
    s.progress.completedClassIds.join(",") +
    "|" +
    s.progress.passedExamIds.join(",");
  if (progSig !== lastProgressSig) {
    lastProgressSig = progSig;
    saveProgressEverywhere(s.progress);
  }

  const deviceKey =
    `${s.midiListening}|` + s.midiDevices.map((d) => d.id + d.state).join("|");
  if (deviceKey !== lastDeviceKey) {
    lastDeviceKey = deviceKey;
    if (s.midiDevices.length === 0) {
      devicesEl.innerHTML =
        "<li>No MIDI keyboard detected. Playing with laptop keys.</li>";
    } else {
      devicesEl.innerHTML = s.midiDevices
        .map((d) => {
          const live = s.midiListening && s.playInput === "midi" ? " · active" : "";
          return `<li><strong>${escapeHtml(d.name)}</strong> <span style="opacity:.6">(${escapeHtml(d.manufacturer || "—")}) · ${d.state}${live}</span></li>`;
        })
        .join("");
    }
    // Re-match on-screen keyboard when MIDI devices change
    applyKeyboardDisplay(expected.concat(s.exam.expectedMidis ?? []));
  }

  const guideMidis =
    s.blindMode || s.exam.phase === "running"
      ? (s.exam.expectedMidis ?? [])
      : expected;
  const kbSig = `${kbMode}|${s.playInput}|${s.qwertyEnabled}|${s.midiDevices.map((d) => d.name).join(";")}|${guideMidis.join(",")}|${observedMidiMin}-${observedMidiMax}`;
  if (kbSig !== lastKbSig) {
    lastKbSig = kbSig;
    applyKeyboardDisplay(guideMidis);
  }

  btnMute.textContent = s.audioMuted ? "Unmute" : "Mute";
  btnMidiDisconnect.disabled = !s.midiListening || s.midiDevices.length === 0;
  btnMetro.textContent = s.metronome.running ? "Stop" : "Start";

  if (s.audioReady && s.outputLatencyMs > 0) {
    latencyHint.textContent = `Realtime measured total ≈ ${s.outputLatencyMs.toFixed(0)} ms (Web Audio + OS mixer)`;
    if (latencyDetail) {
      latencyDetail.textContent =
        `Live breakdown · baseLatency ${s.baseLatencyMs.toFixed(1)} ms · output buffer ${s.outputBufferMs.toFixed(1)} ms` +
        (s.outputBufferMs <= 0
          ? " · (this browser does not expose outputLatency — total may under-report)"
          : " · updates every frame while audio is running");
    }
  } else if (!s.audioReady) {
    latencyHint.textContent = "Tap a piano key or press A–L once — audio unlocks automatically.";
    if (latencyDetail) {
      latencyDetail.textContent =
        "Measured output buffer appears here after unlock (Chrome/Edge report it; Safari often hides it).";
    }
  }
  const backend = s.audioBackend ?? "none";
  const desk = (window as unknown as { pianoTutorDesktop?: { shell?: string; platform?: string } })
    .pianoTutorDesktop;
  audioBackendHint.textContent =
    backend === "native"
      ? desk?.shell === "electron"
        ? desk.platform === "darwin"
          ? "Audio: macOS Core Audio low-buffer piano (free) · metronome/SFX still Web Audio"
          : "Audio: Desktop winmm low-buffer piano (free) · metronome/SFX still Web Audio"
        : "Audio: Native AudioTrack (low-latency Android engine) · piano notes bypass WebView buffer"
      : backend === "worklet"
        ? `Audio: AudioWorklet native render thread${desk?.shell === "electron" ? " · Electron (fallback)" : ""} · synth runs off the UI thread`
        : backend === "main-thread"
          ? "Audio: main-thread oscillators (Worklet unavailable in this browser)"
          : "Audio: waiting for unlock";
}

function pushLog(e: TutorNoteEvent): void {
  if (e.type !== "noteon") return;
  observedMidiMin = Math.min(observedMidiMin, e.midiNote);
  observedMidiMax = Math.max(observedMidiMax, e.midiNote);
  if (kbMode === "auto") {
    const next = snapObservedRangeToKeyboard(observedMidiMin, observedMidiMax);
    if (next !== kbResolved) applyKeyboardDisplay();
  }
  const { name, octave } = midiToNoteName(e.midiNote);
  let extra = "";
  if (e.sync?.scored) {
    const sign = e.sync.deltaMs >= 0 ? "+" : "";
    extra = `  sync=${e.sync.quality} (${sign}${e.sync.deltaMs.toFixed(0)}ms)`;
  }
  const line = document.createElement("div");
  line.textContent = `NOTEON  ${name}${octave}  midi=${e.midiNote}  vel=${e.velocity}  [${e.source}]${extra}`;
  logEl.prepend(line);
  while (logEl.childElementCount > 24) logEl.lastElementChild?.remove();
}

piano.onPointerNote = (type, midi) => {
  if (type === "noteon") core.playNoteImmediate(midi, 100);
  else core.releaseNote(midi);
};

btnMute.addEventListener("click", async () => {
  const muted = !core.getStatus().audioMuted;
  await core.setAudioMuted(muted);
});

btnPanic.addEventListener("click", () => core.panic());

btnMidiConnect.addEventListener("click", async () => {
  await core.connectMidi();
});

btnMidiDisconnect.addEventListener("click", () => {
  core.disconnectMidi();
});

playInput.addEventListener("change", () => {
  core.setPlayInput(playInput.value as PlayInputMode, { userChoice: true });
});

btnMetro.addEventListener("click", async () => {
  if (core.getStatus().metronome.running) {
    core.stopMetronome();
  } else {
    await core.startMetronome();
  }
});

btnMetroReset.addEventListener("click", () => {
  core.resetMetronomeTransport();
  for (const n of pulseRow.children) (n as HTMLElement).classList.remove("on");
});

vol.addEventListener("input", () => {
  core.setMasterVolume(Number(vol.value) / 100);
});
vol.addEventListener("pointerup", () => vol.blur());
vol.addEventListener("change", () => vol.blur());
metroVol.addEventListener("pointerup", () => metroVol.blur());
bpm.addEventListener("pointerup", () => bpm.blur());

bpm.addEventListener("input", () => {
  const v = Number(bpm.value);
  bpmVal.textContent = String(v);
  core.setMetronomeBpm(v);
});

timesig.addEventListener("change", () => {
  const id = timesig.value as TimeSignatureId;
  core.setMetronomeTimeSignature(id);
  rebuildPulseLights(id);
});

metroVol.addEventListener("input", () => {
  core.setMetronomeVolume(Number(metroVol.value) / 100);
});

lockGrade.addEventListener("change", () => {
  core.setMetronomeLockGrading(lockGrade.checked);
});

core.onStatus(renderStatus);
core.onNote(pushLog);
core.onMetronomeBeat(flashPulse);

try {
  core.setProgressState(readLocalProgress());
} catch {
  /* ignore */
}

bindProgressCloudSync((progress) => {
  core.setProgressState(progress);
  refreshLevelMap();
  const focused = classSelect.value;
  if (focused && progress.completedClassIds.includes(focused)) {
    const nextId = pickNextCampaignClassId(focused);
    if (nextId) {
      const next = getClassById(nextId);
      focusCampaignClass(nextId, next?.defaultMinutes);
    }
  }
  renderStatus(core.getStatus());
});

const probe = new TutorProbe(core as never);
(window as unknown as { __pianoTutorProbe: TutorProbe; __pianoTutorCore: typeof core }).__pianoTutorProbe =
  probe;
(window as unknown as { __pianoTutorCore: typeof core }).__pianoTutorCore = core;

void core.start().then(async (s) => {
  renderStatus(s);
  if (isNativePianoAudioAvailable()) {
    const info = await nativePianoStart();
    if (info?.backend === "audiotrack") {
      core.synth.setNativeSink({
        noteOn: nativePianoNoteOn,
        noteOff: nativePianoNoteOff,
        allNotesOff: nativePianoAllNotesOff,
        setMasterVolume: nativePianoSetMasterVolume,
        setMuted: nativePianoSetMuted,
      });
      renderStatus(core.getStatus());
    }
  } else if (isDesktopPianoAvailable()) {
    // Native piano OFF by default (winmm blast on Windows). Website AudioWorklet only.
    // Debug: localStorage.setItem("uptForceNativePiano","1") AND env PIANO_TUTOR_NATIVE_PIANO=1
    const forceNative =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("uptForceNativePiano") === "1";
    if (forceNative) {
      try {
        const info = await desktopPianoStart();
        if (info?.ok && (info.backend === "winmm" || info.backend === "coreaudio")) {
          core.synth.setNativeSink({
            noteOn: desktopPianoNoteOn,
            noteOff: desktopPianoNoteOff,
            allNotesOff: desktopPianoAllNotesOff,
            setMasterVolume: desktopPianoSetMasterVolume,
            setMuted: desktopPianoSetMuted,
          });
          renderStatus(core.getStatus());
        }
      } catch {
        /* worklet */
      }
    }
  }
  void core.ensureAudio().catch(() => {
    /* browsers may reject until a real gesture — that's OK */
  });
  // Soft store update notice (Android / Windows). Free hosting JSON — no push.
  window.setTimeout(() => {
    void import("./updateCheck").then(async ({ checkForStoreUpdate, showUpdateAvailableModal }) => {
      try {
        const offer = await checkForStoreUpdate();
        if (offer) showUpdateAvailableModal(offer);
      } catch {
        /* ignore */
      }
    });
  }, 2500);
});

async function resumeAudioIfNeeded(): Promise<void> {
  try {
    await core.synth.resume();
  } catch {
    /* ignore */
  }
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void resumeAudioIfNeeded();
});

void import("@capacitor/app")
  .then(async ({ App }) => {
    const applyDeepLink = (url: string) => {
      void import("./firebase/authAccount").then(({ finishAndroidGoogleAuthFromUrl }) =>
        finishAndroidGoogleAuthFromUrl(url)
          .then((ok) => {
            if (ok) {
              comboBanner.textContent = "Signed in — profile filled from Google.";
              void refreshStreakBar();
            }
          })
          .catch((e) => {
            comboBanner.textContent = e instanceof Error ? e.message : "Sign-in failed.";
          }),
      );
    };

    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void resumeAudioIfNeeded();
        void import("./firebase/androidAuthBridge").then(({ resumePendingAndroidAuthHandoff }) =>
          resumePendingAndroidAuthHandoff().then((ok) => {
            if (ok) {
              comboBanner.textContent = "Signed in — profile filled from Google.";
              void refreshStreakBar();
            }
          }),
        );
      }
    });
    void App.addListener("appUrlOpen", ({ url }) => {
      applyDeepLink(url);
    });
    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) applyDeepLink(launch.url);
    } catch {
      /* ignore */
    }
    void import("./firebase/androidAuthBridge").then(({ resumePendingAndroidAuthHandoff }) =>
      resumePendingAndroidAuthHandoff().then((ok) => {
        if (ok) {
          comboBanner.textContent = "Signed in — profile filled from Google.";
          void refreshStreakBar();
        }
      }),
    );
  })
  .catch(() => {
    /* web / Electron — Capacitor App plugin unavailable */
  });

/* ── Tour ── */
document.querySelector("#btn-tour")?.addEventListener("click", () => {
  startTour((screen) => void enterMode(screen, false));
});
bindHelpButtons();
// Auto-tour on first visit (after a small delay so splash can finish)
setTimeout(() => {
  if (shouldAutoTour()) startTour((screen) => void enterMode(screen, false));
}, 2500);

/* ── Streaks (Google account only — no guest / signed-out fake streak) ── */
async function refreshStreakBar(): Promise<void> {
  const streakSlot = document.querySelector("#streak-bar-slot");
  if (!streakSlot) return;
  if (!isFirebaseConfigured || !streakAccountUid()) {
    streakSlot.innerHTML = isFirebaseConfigured ? renderStreakSignInGate() : "";
    return;
  }
  const streak = await loadStreak();
  streakSlot.innerHTML = renderStreakBar(streak);
}

void refreshStreakBar();

// Record practice on class complete and imported piece complete
core.onStatus((s) => {
  if (s.classSession.phase === "completed") void recordPractice().then(() => refreshStreakBar());
});
